import type { CommandePresentee, ReglagesBoutique, OeuvreBoutique } from './boutique.js';
import type { JetonPresente } from './accesApi.js';
import type {
  ListeNewsletter,
  CampagneNewsletter,
  VisuelNewsletter,
  ReglagesNewsletter,
} from './newsletter.js';
import type { FluxDiffusion, EtatOeuvreDiffusion } from './diffusion.js';
import type { EvenementAgenda, RendezVousPresente } from './agenda.js';
import type { ArticlePresente } from './presse.js';
import type { ProvenancePresentee, CoutPresente } from './provenance.js';
import type { DossierOeuvresPresente } from './dossiersOeuvres.js';
import type { ConsoleGroupe } from '@artosera/shared';
import type { SallePresentee, OeuvreDeLaSalle } from './viewingRooms.js';
import type { CatalogueVisitePresente } from './catalogueVisite.js';
import { echeanceParDefaut } from '@artosera/shared';
import type { ReponseConnecteurs } from './connecteurs.js';
import type { MouvementPresente } from './mouvements.js';
import type { EtatOffre, OffrePresentee } from './offres.js';
import type { SiteData } from '@artosera/site-renderer';
import type { ArchivePresentee } from './archives.js';
import type { ArtistePresente } from './artistes.js';
import type { AccesPresente, DocumentPresente } from './coffre.js';
import type { ContactPresente } from './contacts.js';
import type { DemandePresentee } from './demandes.js';
import type { CertificatPresente } from './documentsGalerie.js';
import type { IdentiteSso } from './sso.js';
import type { MembreEquipe } from './equipe.js';
import type { LieuPresente } from './lieux.js';
import type { MediaPresente } from './medias.js';
import type { MediaDeLOeuvre, OeuvrePresentee } from './oeuvres.js';
import type { ExpositionPresentee } from './expositions.js';
import type { ActualitePresentee } from './actualites.js';
import type { ReservationPresentee } from './reservations.js';
import type { PageSite, PublicationPresentee, ReponseSite } from './site.js';
import type {
  FacturePresentee,
  ModeleDocument,
  ProfilFiscal,
  ReglementPresente,
} from './ventes.js';

import heuresPales from './demo-artworks/les-heures-pales.svg?url';
import releve from './demo-artworks/releve.svg?url';
import bassin from './demo-artworks/bassin.svg?url';

/** Illustrations fictives locales de la maquette Halo, réutilisées pour les données de démonstration. */
const ILLUSTRATIONS_DEMO = [heuresPales, releve, bassin] as const;

export const DEMO_GALERIE = 'sainte-avoye';

export const DEMO_ARCHIVES: readonly ArchivePresentee[] = [
  {
    id: 'archive-1',
    galerie: {
      id: 'demo-galerie-1',
      slug: DEMO_GALERIE,
      nom: 'Galerie Sainte-Avoye',
      etat: 'active',
    },
    origine: 'mensuel',
    final: false,
    etat: 'pret',
    pretLe: '2026-09-01T03:00:00.000Z',
    octets: 734_003_200,
    sha256: '9f2c4b7a1e0d3c5b8a6f4e2d1c0b9a8f7e6d5c4b3a291807f6e5d4c3b2a19080',
    nomFichier: 'sainte-avoye-2026-09-01.zip',
    lienExpireLe: null,
    telechargeeParMoi: false,
    telechargementsParMoi: 0,
    accuseeParMoi: false,
    mfaRequise: false,
  },
];

export const DEMO_LIEUX: LieuPresente[] = [
  {
    id: 'lieu-1',
    nom: 'Salle 2',
    type: 'interne',
    adresse: null,
    contactId: null,
    notes: '',
    actif: true,
    creeLe: '2024-01-10T09:00:00.000Z',
    modifieLe: '2024-01-10T09:00:00.000Z',
  },
  {
    id: 'lieu-2',
    nom: 'Réserve',
    type: 'interne',
    adresse: null,
    contactId: null,
    notes: 'Tiroir 3, étagère du fond.',
    actif: true,
    creeLe: '2024-01-10T09:00:00.000Z',
    modifieLe: '2024-01-10T09:00:00.000Z',
  },
  {
    id: 'lieu-3',
    nom: 'Foire de la Cimaise 2026',
    type: 'foire',
    adresse: {
      libelle: 'Grand Palais',
      ligne1: '3 avenue du Général Eisenhower',
      ligne2: '',
      codePostal: '75008',
      ville: 'Paris',
      pays: 'FR',
    },
    contactId: null,
    notes: '',
    actif: true,
    creeLe: '2026-08-01T09:00:00.000Z',
    modifieLe: '2026-08-01T09:00:00.000Z',
  },
  {
    id: 'lieu-4',
    nom: 'En dépôt — Galerie Rive Gauche',
    type: 'externe',
    adresse: null,
    contactId: null,
    notes: '',
    actif: true,
    creeLe: '2024-03-01T09:00:00.000Z',
    modifieLe: '2024-03-01T09:00:00.000Z',
  },
];

export const DEMO_ARTISTES: ArtistePresente[] = [
  {
    id: 'artiste-1',
    nom: 'Léa Bertrand',
    slug: 'lea-bertrand',
    naissance: '1987, Rennes',
    nationalite: 'FR',
    biographie: {
      fr: 'Léa Bertrand peint la lumière déclinante des intérieurs bourgeois, entre mémoire et disparition.',
      en: 'Léa Bertrand paints the fading light of bourgeois interiors, between memory and disappearance.',
    },
    cv: { fr: '2019 — Prix de la Jeune Peinture\n2016 — Diplôme des Beaux-Arts de Rennes' },
    presse: { fr: '' },
    portraitId: null,
    representation: {
      representee: true,
      depuis: '2019-04-01T00:00:00.000Z',
      jusquau: null,
      exclusivite: true,
      contratId: null,
    },
    ordre: 0,
    ordreOeuvres: ['oeuvre-1', 'oeuvre-2'],
    visibleSurLeSite: true,
    archive: false,
    creeLe: '2019-04-01T09:00:00.000Z',
    modifieLe: '2024-01-10T09:00:00.000Z',
  },
  {
    id: 'artiste-2',
    nom: 'Hugo Massin',
    slug: 'hugo-massin',
    naissance: '1979, Lyon',
    nationalite: 'FR',
    biographie: {
      fr: 'Hugo Massin construit ses toiles comme des accrochages : citations, cadrages, vernissages fantômes.',
    },
    cv: { fr: '2022 — Résidence à la Villa Médicis' },
    presse: { fr: '' },
    portraitId: null,
    representation: {
      representee: true,
      depuis: '2021-09-01T00:00:00.000Z',
      jusquau: null,
      exclusivite: false,
      contratId: null,
    },
    ordre: 1,
    ordreOeuvres: ['oeuvre-3', 'oeuvre-4'],
    visibleSurLeSite: true,
    archive: false,
    creeLe: '2021-09-01T09:00:00.000Z',
    modifieLe: '2021-09-01T09:00:00.000Z',
  },
  {
    id: 'artiste-3',
    nom: 'Inès Auber',
    slug: 'ines-auber',
    naissance: '1991, Marseille',
    nationalite: 'FR',
    biographie: {
      fr: 'Inès Auber travaille le bronze et l’estampe, en séries courtes et numérotées.',
    },
    cv: { fr: '' },
    presse: { fr: '' },
    portraitId: null,
    representation: {
      representee: false,
      depuis: null,
      jusquau: null,
      exclusivite: false,
      contratId: null,
    },
    ordre: 2,
    ordreOeuvres: ['oeuvre-5', 'oeuvre-6'],
    visibleSurLeSite: true,
    archive: false,
    creeLe: '2022-02-01T09:00:00.000Z',
    modifieLe: '2022-02-01T09:00:00.000Z',
  },
];

export const DEMO_OEUVRES: OeuvrePresentee[] = [
  {
    id: 'oeuvre-1',
    titre: 'Les heures pâles',
    slug: 'les-heures-pales',
    slugPublie: 'les-heures-pales',
    artisteId: 'artiste-1',
    artisteNom: 'Léa Bertrand',
    annee: 2024,
    technique: { fr: 'Huile sur toile', en: 'Oil on canvas' },
    dimensions: { hauteur: 130, largeur: 97, uniteOrigine: 'cm' },
    type: 'unique',
    tirage: null,
    numeroInventaire: 'SAV-2024-017',
    prixPublicCentimes: 1_250_000,
    devise: 'EUR',
    regimeTva: 'marge',
    droitDeSuite: true,
    statut: 'reservee',
    localisationId: 'lieu-1',
    mediaPrincipalId: 'media-1',
    visibleSurLeSite: true,
    prixVisible: true,
    description: {
      fr: 'Une pièce de la série « Intérieurs » : la lumière de fin d’après-midi sur un salon vidé de ses meubles.',
      en: 'A piece from the “Interiors” series: late-afternoon light on a living room emptied of its furniture.',
    },
    notesInternes: 'Réservée par M. Lenoir, à confirmer avant le 12.',
    etiquettes: ['intérieurs', 'lumière'],
    creeLe: '2024-05-01T09:00:00.000Z',
    modifieLe: '2026-09-10T09:00:00.000Z',
    prixArtisteCentimes: 620_000,
    prixAchatCentimes: 400_000,
    margeCentimes: 630_000,
  },
  {
    id: 'oeuvre-2',
    titre: 'Courtesy',
    slug: 'courtesy',
    slugPublie: null,
    artisteId: 'artiste-1',
    artisteNom: 'Léa Bertrand',
    annee: 2023,
    technique: { fr: 'Huile sur toile', en: 'Oil on canvas' },
    dimensions: { hauteur: 90, largeur: 70, uniteOrigine: 'cm' },
    type: 'unique',
    tirage: null,
    numeroInventaire: 'SAV-2024-018',
    prixPublicCentimes: 820_000,
    devise: 'EUR',
    regimeTva: 'marge',
    droitDeSuite: true,
    statut: 'disponible',
    localisationId: 'lieu-3',
    mediaPrincipalId: 'media-2',
    visibleSurLeSite: false,
    prixVisible: false,
    description: { fr: '' },
    notesInternes: '',
    etiquettes: [],
    creeLe: '2023-11-01T09:00:00.000Z',
    modifieLe: '2023-11-01T09:00:00.000Z',
    prixArtisteCentimes: 400_000,
    prixAchatCentimes: 250_000,
    margeCentimes: 420_000,
  },
  {
    id: 'oeuvre-3',
    titre: 'Vernissage',
    slug: 'vernissage',
    slugPublie: 'vernissage',
    artisteId: 'artiste-2',
    artisteNom: 'Hugo Massin',
    annee: 2023,
    technique: { fr: 'Acrylique sur bois', en: 'Acrylic on wood' },
    dimensions: { hauteur: 60, largeur: 60, uniteOrigine: 'cm' },
    type: 'unique',
    tirage: null,
    numeroInventaire: 'SAV-2023-004',
    prixPublicCentimes: 450_000,
    devise: 'EUR',
    regimeTva: 'normal',
    droitDeSuite: false,
    statut: 'vendue',
    localisationId: null,
    mediaPrincipalId: 'media-3',
    visibleSurLeSite: true,
    prixVisible: false,
    description: { fr: 'Un accrochage vu depuis la réserve, cadré par la porte entrebâillée.' },
    notesInternes: 'Vendue à la Foire de la Cimaise 2025.',
    etiquettes: ['citation', 'huis clos'],
    creeLe: '2023-04-01T09:00:00.000Z',
    modifieLe: '2025-10-01T09:00:00.000Z',
    prixArtisteCentimes: 220_000,
    prixAchatCentimes: 150_000,
    margeCentimes: 230_000,
  },
  {
    id: 'oeuvre-4',
    titre: 'Étude pour un accrochage',
    slug: 'etude-pour-un-accrochage',
    slugPublie: null,
    artisteId: 'artiste-2',
    artisteNom: 'Hugo Massin',
    annee: 2022,
    technique: { fr: 'Fusain sur papier', en: 'Charcoal on paper' },
    dimensions: { hauteur: 40, largeur: 30, uniteOrigine: 'cm' },
    type: 'unique',
    tirage: null,
    numeroInventaire: 'SAV-2022-011',
    prixPublicCentimes: 180_000,
    devise: 'EUR',
    regimeTva: 'normal',
    droitDeSuite: false,
    statut: 'disponible',
    localisationId: 'lieu-3',
    mediaPrincipalId: 'media-4',
    visibleSurLeSite: false,
    prixVisible: false,
    description: { fr: '' },
    notesInternes: '',
    etiquettes: [],
    creeLe: '2022-06-01T09:00:00.000Z',
    modifieLe: '2022-06-01T09:00:00.000Z',
    prixArtisteCentimes: 90_000,
    prixAchatCentimes: 60_000,
    margeCentimes: 90_000,
  },
  {
    id: 'oeuvre-5',
    titre: 'Consignation',
    slug: 'consignation',
    slugPublie: null,
    artisteId: 'artiste-3',
    artisteNom: 'Inès Auber',
    annee: 2024,
    technique: { fr: 'Bronze, épreuve 2/8', en: 'Bronze, edition 2/8' },
    dimensions: { hauteur: 55, largeur: 22, profondeur: 18, uniteOrigine: 'cm' },
    type: 'edition',
    tirage: { taille: 8, epreuvesArtiste: 2, epreuvesEssai: 1, exemplairesSuivis: true, vendus: 1 },
    numeroInventaire: 'SAV-2024-002',
    prixPublicCentimes: 3_400_000,
    devise: 'EUR',
    regimeTva: 'normal',
    droitDeSuite: true,
    statut: 'en_depot',
    localisationId: 'lieu-4',
    mediaPrincipalId: 'media-5',
    visibleSurLeSite: true,
    prixVisible: true,
    description: {
      fr: 'Épreuve en dépôt chez un confrère parisien, retour prévu en fin de trimestre.',
    },
    notesInternes: '',
    etiquettes: ['bronze', 'édition'],
    creeLe: '2024-02-01T09:00:00.000Z',
    modifieLe: '2026-06-01T09:00:00.000Z',
    prixArtisteCentimes: 1_700_000,
    prixAchatCentimes: 900_000,
    margeCentimes: 1_700_000,
  },
  {
    id: 'oeuvre-6',
    titre: 'Nature morte, épreuve EA',
    slug: 'nature-morte-epreuve-ea',
    slugPublie: null,
    artisteId: 'artiste-3',
    artisteNom: 'Inès Auber',
    annee: 2021,
    technique: { fr: 'Lithographie, EA', en: 'Lithograph, artist’s proof' },
    dimensions: { hauteur: 50, largeur: 65, uniteOrigine: 'cm' },
    type: 'edition',
    tirage: {
      taille: 20,
      epreuvesArtiste: 3,
      epreuvesEssai: 0,
      exemplairesSuivis: false,
      vendus: 14,
    },
    numeroInventaire: 'SAV-2021-030',
    prixPublicCentimes: 95_000,
    devise: 'EUR',
    regimeTva: 'normal',
    droitDeSuite: false,
    statut: 'retiree',
    localisationId: null,
    mediaPrincipalId: 'media-6',
    visibleSurLeSite: false,
    prixVisible: false,
    description: { fr: '' },
    notesInternes: 'Retirée à la demande de l’artiste, série close.',
    etiquettes: [],
    creeLe: '2021-01-01T09:00:00.000Z',
    modifieLe: '2025-01-01T09:00:00.000Z',
    prixArtisteCentimes: 45_000,
    prixAchatCentimes: 30_000,
    margeCentimes: 50_000,
  },
  {
    id: 'oeuvre-7',
    titre: 'Pièce prêtée, musée régional',
    slug: 'piece-pretee-musee-regional',
    slugPublie: null,
    artisteId: 'artiste-1',
    artisteNom: 'Léa Bertrand',
    annee: 2020,
    technique: { fr: 'Huile sur toile' },
    dimensions: { hauteur: 100, largeur: 80, uniteOrigine: 'cm' },
    type: 'unique',
    tirage: null,
    numeroInventaire: 'SAV-2020-009',
    prixPublicCentimes: 980_000,
    devise: 'EUR',
    regimeTva: 'marge',
    droitDeSuite: true,
    statut: 'pretee',
    localisationId: null,
    mediaPrincipalId: null,
    visibleSurLeSite: false,
    prixVisible: false,
    description: { fr: '' },
    notesInternes: 'Prêtée au musée régional jusqu’en mars.',
    etiquettes: [],
    creeLe: '2020-09-01T09:00:00.000Z',
    modifieLe: '2026-01-01T09:00:00.000Z',
    prixArtisteCentimes: 480_000,
    prixAchatCentimes: 300_000,
    margeCentimes: 500_000,
  },
  {
    id: 'oeuvre-8',
    titre: 'Archive d’atelier n°3',
    slug: 'archive-atelier-3',
    slugPublie: null,
    artisteId: 'artiste-2',
    artisteNom: 'Hugo Massin',
    annee: 2018,
    technique: { fr: 'Encre sur papier' },
    dimensions: { hauteur: 30, largeur: 21, uniteOrigine: 'cm' },
    type: 'unique',
    tirage: null,
    numeroInventaire: 'SAV-2018-002',
    prixPublicCentimes: 65_000,
    devise: 'EUR',
    regimeTva: 'normal',
    droitDeSuite: false,
    statut: 'archivee',
    localisationId: null,
    mediaPrincipalId: null,
    visibleSurLeSite: false,
    prixVisible: false,
    description: { fr: '' },
    notesInternes: 'Archivée : pièce d’étude, hors catalogue.',
    etiquettes: [],
    creeLe: '2018-01-01T09:00:00.000Z',
    modifieLe: '2024-01-01T09:00:00.000Z',
    prixArtisteCentimes: 30_000,
    prixAchatCentimes: 20_000,
    margeCentimes: 35_000,
  },
];

export const DEMO_MEDIAS: Record<string, MediaDeLOeuvre[]> = Object.fromEntries(
  DEMO_OEUVRES.filter((oeuvre) => oeuvre.mediaPrincipalId !== null).map((oeuvre) => [
    oeuvre.id,
    [
      {
        id: oeuvre.mediaPrincipalId as string,
        cleOriginale: `demo/${oeuvre.slug}.jpg`,
        variantes: [
          { cle: `demo/${oeuvre.slug}-800.jpg`, largeur: 800, hauteur: 1000, format: 'webp' },
        ],
        type: 'vue',
        legende: { fr: oeuvre.titre },
        credit: null,
        ordre: 0,
      },
    ],
  ]),
);

export const DEMO_VIGNETTES: Record<string, string> = Object.fromEntries(
  DEMO_OEUVRES.filter((oeuvre) => oeuvre.mediaPrincipalId !== null).map((oeuvre, index) => [
    oeuvre.mediaPrincipalId as string,
    ILLUSTRATIONS_DEMO[index % ILLUSTRATIONS_DEMO.length] as string,
  ]),
);

/**
 * Carnet de démonstration (S3-c) : de quoi peupler la liste, les filtres
 * (type, étiquette, origine, consentement, archivés), et la fiche — un
 * contact anonymisé (7.7) compris, pour vérifier l'affichage « Contact
 * supprimé » en lecture seule sans avoir à jouer la suppression.
 */
export const DEMO_CONTACTS: ContactPresente[] = [
  {
    id: 'contact-1',
    genre: 'personne',
    nom: 'Lenoir',
    prenom: 'Étienne',
    raisonSociale: null,
    nomAffiche: 'Étienne Lenoir',
    type: 'collectionneur',
    adresses: [
      {
        libelle: 'Domicile',
        ligne1: '12 rue des Beaux-Arts',
        ligne2: '',
        codePostal: '75006',
        ville: 'Paris',
        pays: 'FR',
      },
    ],
    emails: ['etienne.lenoir@example.com'],
    telephones: ['+33 6 12 34 56 78'],
    langue: 'fr',
    numeroTvaIntracom: null,
    consentementNewsletter: {
      accorde: true,
      date: '2025-03-04T10:00:00.000Z',
      source: 'regie',
      retireLe: null,
    },
    notes: 'Collectionne les séries « Intérieurs » ; préfère être appelé plutôt qu’écrit.',
    etiquettes: ['VIP', 'habitué'],
    origine: 'regie',
    archive: false,
    anonymise: false,
    anonymiseLe: null,
    fusionneDans: null,
    creeLe: '2024-02-01T09:00:00.000Z',
    modifieLe: '2026-09-10T09:00:00.000Z',
  },
  {
    id: 'contact-2',
    genre: 'organisation',
    nom: 'Fondation Meraux',
    prenom: null,
    raisonSociale: 'Fondation Meraux pour l’art contemporain',
    nomAffiche: 'Fondation Meraux pour l’art contemporain',
    type: 'institution',
    adresses: [
      {
        libelle: 'Siège',
        ligne1: '4 quai Voltaire',
        ligne2: '',
        codePostal: '75007',
        ville: 'Paris',
        pays: 'FR',
      },
    ],
    emails: ['contact@fondation-meraux.example'],
    telephones: [],
    langue: 'fr',
    numeroTvaIntracom: 'FR40123456789',
    consentementNewsletter: {
      accorde: false,
      date: null,
      source: null,
      retireLe: null,
    },
    notes: '',
    etiquettes: ['institutionnel'],
    origine: 'regie',
    archive: false,
    anonymise: false,
    anonymiseLe: null,
    fusionneDans: null,
    creeLe: '2023-11-12T09:00:00.000Z',
    modifieLe: '2025-06-01T09:00:00.000Z',
  },
  {
    id: 'contact-3',
    genre: 'personne',
    nom: 'Coste',
    prenom: 'Margaux',
    raisonSociale: null,
    nomAffiche: 'Margaux Coste',
    type: 'presse',
    adresses: [],
    emails: ['margaux.coste@example.com'],
    telephones: ['+33 6 98 76 54 32'],
    langue: 'fr',
    numeroTvaIntracom: null,
    consentementNewsletter: {
      accorde: true,
      date: '2026-01-15T08:30:00.000Z',
      source: 'site',
      retireLe: null,
    },
    notes: 'Critique pour une revue d’art ; a demandé les visuels de « Vernissage ».',
    etiquettes: ['presse'],
    origine: 'site',
    archive: false,
    anonymise: false,
    anonymiseLe: null,
    fusionneDans: null,
    creeLe: '2026-01-15T08:30:00.000Z',
    modifieLe: '2026-01-15T08:30:00.000Z',
  },
  {
    id: 'contact-4',
    genre: 'personne',
    nom: 'Ridel',
    prenom: 'Bastien',
    raisonSociale: null,
    nomAffiche: 'Bastien Ridel',
    type: 'collectionneur',
    adresses: [
      {
        libelle: '',
        ligne1: '9 avenue Foch',
        ligne2: 'Appartement 3',
        codePostal: '69006',
        ville: 'Lyon',
        pays: 'FR',
      },
    ],
    emails: ['bastien.ridel@example.com'],
    telephones: [],
    langue: 'fr',
    numeroTvaIntracom: null,
    consentementNewsletter: {
      accorde: false,
      date: '2025-05-02T09:00:00.000Z',
      source: 'regie',
      retireLe: '2026-02-20T09:00:00.000Z',
    },
    notes: '',
    etiquettes: [],
    origine: 'regie',
    archive: false,
    anonymise: false,
    anonymiseLe: null,
    fusionneDans: null,
    creeLe: '2025-05-02T09:00:00.000Z',
    modifieLe: '2026-02-20T09:00:00.000Z',
  },
  {
    id: 'contact-5',
    genre: 'personne',
    nom: 'Ancien doublon',
    prenom: null,
    raisonSociale: null,
    nomAffiche: 'Ancien doublon',
    type: 'collectionneur',
    adresses: [],
    emails: ['doublon@example.com'],
    telephones: [],
    langue: 'fr',
    numeroTvaIntracom: null,
    consentementNewsletter: { accorde: false, date: null, source: null, retireLe: null },
    notes: '',
    etiquettes: [],
    origine: 'regie',
    archive: true,
    anonymise: false,
    anonymiseLe: null,
    fusionneDans: 'contact-1',
    creeLe: '2023-01-01T09:00:00.000Z',
    modifieLe: '2024-02-01T09:00:00.000Z',
  },
  {
    id: 'contact-6',
    genre: 'personne',
    nom: 'Contact supprimé',
    prenom: null,
    raisonSociale: null,
    nomAffiche: 'Contact supprimé',
    type: 'collectionneur',
    adresses: [],
    emails: [],
    telephones: [],
    langue: 'fr',
    numeroTvaIntracom: null,
    consentementNewsletter: { accorde: false, date: null, source: null, retireLe: null },
    notes: '',
    etiquettes: [],
    origine: 'regie',
    archive: true,
    anonymise: true,
    anonymiseLe: '2026-04-18T09:00:00.000Z',
    fusionneDans: null,
    creeLe: '2022-06-01T09:00:00.000Z',
    modifieLe: '2026-04-18T09:00:00.000Z',
  },
];

/** Demandes de démonstration (S3-c) : les trois états, assignées ou non. */
export const DEMO_DEMANDES: DemandePresentee[] = [
  {
    id: 'demande-1',
    expositionId: null,
    oeuvreId: 'oeuvre-1',
    contactId: 'contact-1',
    nom: 'Étienne Lenoir',
    email: 'etienne.lenoir@example.com',
    telephone: '+33 6 12 34 56 78',
    langue: 'fr',
    sujet: 'Les heures pâles',
    message: 'Toujours intéressé par cette pièce si la réservation en cours ne va pas à son terme.',
    source: 'regie',
    etat: 'a_traiter',
    assigneeA: null,
    note: '',
    recueLe: '2026-09-15T14:20:00.000Z',
    repondueLe: null,
    creeeLe: '2026-09-15T14:20:00.000Z',
    modifieeLe: '2026-09-15T14:20:00.000Z',
  },
  {
    id: 'demande-2',
    expositionId: null,
    oeuvreId: 'oeuvre-3',
    contactId: 'contact-3',
    nom: 'Margaux Coste',
    email: 'margaux.coste@example.com',
    telephone: '+33 6 98 76 54 32',
    langue: 'fr',
    sujet: 'Vernissage',
    message: 'Pourriez-vous m’envoyer les visuels haute définition pour un article à paraître ?',
    source: 'site',
    etat: 'a_traiter',
    assigneeA: 'demo-compte-1',
    note: 'Visuels envoyés par e-mail, en attente de sa relecture.',
    recueLe: '2026-09-12T09:05:00.000Z',
    repondueLe: null,
    creeeLe: '2026-09-12T09:05:00.000Z',
    modifieeLe: '2026-09-14T11:00:00.000Z',
  },
  {
    id: 'demande-3',
    expositionId: null,
    oeuvreId: 'oeuvre-5',
    contactId: 'contact-2',
    nom: 'Fondation Meraux',
    email: 'contact@fondation-meraux.example',
    telephone: '',
    langue: 'fr',
    sujet: 'Consignation',
    message: 'Envisageons une acquisition pour la collection permanente ; merci de nous rappeler.',
    source: 'regie',
    etat: 'repondue',
    assigneeA: 'demo-compte-1',
    note: 'Rendez-vous fixé le 28 septembre.',
    recueLe: '2026-08-30T10:00:00.000Z',
    repondueLe: '2026-09-02T15:40:00.000Z',
    creeeLe: '2026-08-30T10:00:00.000Z',
    modifieeLe: '2026-09-02T15:40:00.000Z',
  },
  {
    id: 'demande-4',
    expositionId: null,
    oeuvreId: null,
    contactId: 'contact-4',
    nom: 'Bastien Ridel',
    email: 'bastien.ridel@example.com',
    telephone: '',
    langue: 'fr',
    sujet: 'Demande générale',
    message: 'Souhaite être tenu informé des prochaines expositions à Lyon.',
    source: 'site',
    etat: 'close',
    assigneeA: null,
    note: '',
    recueLe: '2026-07-01T08:00:00.000Z',
    repondueLe: '2026-07-01T16:00:00.000Z',
    creeeLe: '2026-07-01T08:00:00.000Z',
    modifieeLe: '2026-07-03T09:00:00.000Z',
  },
  {
    id: 'demande-5',
    expositionId: null,
    oeuvreId: 'oeuvre-2',
    contactId: null,
    nom: 'Alexis Moreau',
    email: 'visiteur@example.com',
    telephone: '',
    langue: 'en',
    sujet: 'Courtesy',
    message: 'Is this piece still available? Please send more details.',
    source: 'site',
    etat: 'a_traiter',
    assigneeA: null,
    note: '',
    recueLe: '2026-09-18T18:45:00.000Z',
    repondueLe: null,
    creeeLe: '2026-09-18T18:45:00.000Z',
    modifieeLe: '2026-09-18T18:45:00.000Z',
  },
];

/**
 * Expositions, foires et événements de démonstration (S3-d) : une de chaque
 * état affiché (`annoncee`, `en_cours`, `passee`, `brouillon`), plus une
 * foire avec stand — de quoi peupler chaque groupe de la liste et vérifier
 * lieu/stand à l'écran.
 */
export const DEMO_EXPOSITIONS: ExpositionPresentee[] = [
  {
    id: 'exposition-1',
    type: 'exposition',
    titre: { fr: 'Lumières déclinantes', en: 'Fading Lights' },
    sousTitre: null,
    slug: 'lumieres-declinantes',
    slugPublie: 'lumieres-declinantes',
    debutLe: '2026-10-08T00:00:00.000Z',
    finLe: '2026-11-14T00:00:00.000Z',
    vernissage: { date: '2026-10-08T00:00:00.000Z', debut: '18:30', fin: '21:00', note: null },
    lieuId: 'lieu-1',
    stand: null,
    texte: {
      fr: 'Une exposition personnelle de Léa Bertrand : intérieurs, mémoire, lumière qui se retire.',
      en: 'A solo show by Léa Bertrand: interiors, memory, retreating light.',
    },
    artistes: ['artiste-1'],
    oeuvres: ['oeuvre-1', 'oeuvre-2'],
    etat: 'annoncee',
    visibleSurLeSite: true,
    archive: false,
    creeLe: '2026-06-01T09:00:00.000Z',
    modifieLe: '2026-06-01T09:00:00.000Z',
  },
  {
    id: 'exposition-2',
    type: 'exposition',
    titre: { fr: 'Accrochages fantômes', en: 'Ghost Hangings' },
    sousTitre: null,
    slug: 'accrochages-fantomes',
    slugPublie: 'accrochages-fantomes',
    debutLe: '2026-09-02T00:00:00.000Z',
    finLe: '2026-09-30T00:00:00.000Z',
    vernissage: null,
    lieuId: 'lieu-1',
    stand: null,
    texte: { fr: 'Hugo Massin cite, cadre, rejoue le vernissage — jusqu’à son effacement.' },
    artistes: ['artiste-2'],
    oeuvres: ['oeuvre-3', 'oeuvre-4'],
    etat: 'en_cours',
    visibleSurLeSite: true,
    archive: false,
    creeLe: '2026-05-01T09:00:00.000Z',
    modifieLe: '2026-08-20T09:00:00.000Z',
  },
  {
    id: 'exposition-3',
    type: 'foire',
    titre: { fr: 'Foire de la Cimaise 2026' },
    sousTitre: null,
    slug: 'art-paris-2026',
    slugPublie: 'art-paris-2026',
    debutLe: '2026-04-02T00:00:00.000Z',
    finLe: '2026-04-06T00:00:00.000Z',
    vernissage: null,
    lieuId: 'lieu-3',
    stand: 'B12',
    texte: { fr: 'Stand collectif : bronzes et estampes d’Inès Auber.' },
    artistes: ['artiste-3'],
    oeuvres: ['oeuvre-5', 'oeuvre-6'],
    etat: 'passee',
    visibleSurLeSite: false,
    archive: false,
    creeLe: '2026-01-15T09:00:00.000Z',
    modifieLe: '2026-04-07T09:00:00.000Z',
  },
  {
    id: 'exposition-4',
    type: 'evenement',
    titre: { fr: 'Nocturne de printemps' },
    sousTitre: null,
    slug: 'nocturne-de-printemps',
    slugPublie: null,
    debutLe: '2026-12-04T00:00:00.000Z',
    finLe: '2026-12-04T00:00:00.000Z',
    vernissage: { date: '2026-12-04T00:00:00.000Z', debut: '19:00', fin: null, note: null },
    lieuId: null,
    stand: null,
    texte: null,
    artistes: [],
    oeuvres: [],
    etat: 'brouillon',
    visibleSurLeSite: false,
    archive: false,
    creeLe: '2026-09-10T09:00:00.000Z',
    modifieLe: '2026-09-10T09:00:00.000Z',
  },
];

/**
 * Réservations de démonstration (S3-f) : une active qui expire sous sept
 * jours (« Les heures pâles », cohérente avec `notesInternes` de `oeuvre-1`
 * et `demande-1`), une levée pour vérifier que le tableau de bord ne compte
 * que les actives.
 */
export const DEMO_RESERVATIONS: ReservationPresentee[] = [
  {
    id: 'reservation-1',
    oeuvreId: 'oeuvre-1',
    exemplaireId: null,
    contactId: 'contact-1',
    jusquau: '2026-09-26T00:00:00.000Z',
    note: 'À confirmer avec M. Lenoir avant le terme.',
    etat: 'active',
    leveeLe: null,
    creeeLe: '2026-09-08T09:00:00.000Z',
    modifieeLe: '2026-09-08T09:00:00.000Z',
  },
  {
    id: 'reservation-2',
    oeuvreId: 'oeuvre-3',
    exemplaireId: null,
    contactId: 'contact-3',
    jusquau: '2025-10-01T00:00:00.000Z',
    note: '',
    etat: 'levee',
    leveeLe: '2025-10-01T09:00:00.000Z',
    creeeLe: '2025-09-15T09:00:00.000Z',
    modifieeLe: '2025-10-01T09:00:00.000Z',
  },
];

export const DEMO_ACTUALITES: ActualitePresentee[] = [
  {
    id: 'actualite-1',
    titre: { fr: 'Léa Bertrand ouvre « Lumières déclinantes »' },
    slug: 'lea-bertrand-ouvre-lumieres-declinantes',
    slugPublie: 'lea-bertrand-ouvre-lumieres-declinantes',
    chapo: { fr: 'Vernissage le 8 octobre, dès 18h30.' },
    texte: { fr: 'La galerie présente la nouvelle série de Léa Bertrand à partir du 8 octobre.' },
    date: '2026-09-20T00:00:00.000Z',
    artistes: ['artiste-1'],
    expositions: ['exposition-1'],
    visibleSurLeSite: true,
    archive: false,
    creeLe: '2026-09-20T09:00:00.000Z',
    modifieLe: '2026-09-20T09:00:00.000Z',
  },
];

/**
 * Ventes de démonstration (S4-a) : un profil fiscal, un modèle de facture, et
 * cinq documents couvrant les états (brouillon, émise avec règlement
 * partiel, payée, annulée par avoir). Le PDF de démonstration est un vrai
 * PDF/1.4 minimal (`file` le confirme), en `data:` : l'aperçu de la fiche a
 * quelque chose à afficher sans backend.
 */
export const DEMO_PDF_FACTURE_DATA_URI =
  'data:application/pdf;base64,JVBERi0xLjQKMSAwIG9iago8PCAvVHlwZSAvQ2F0YWxvZyAvUGFnZXMgMiAwIFIgPj4KZW5kb2JqCjIgMCBvYmoKPDwgL1R5cGUgL1BhZ2VzIC9LaWRzIFszIDAgUl0gL0NvdW50IDEgPj4KZW5kb2JqCjMgMCBvYmoKPDwgL1R5cGUgL1BhZ2UgL1BhcmVudCAyIDAgUiAvTWVkaWFCb3ggWzAgMCA0MDAgMjYwXSAvQ29udGVudHMgNCAwIFIgL1Jlc291cmNlcyA8PCAvRm9udCA8PCAvRjEgNSAwIFIgPj4gPj4gPj4KZW5kb2JqCjQgMCBvYmoKPDwgL0xlbmd0aCAxMTMgPj4Kc3RyZWFtCkJUIC9GMSAxNiBUZiAyNCAyMDAgVGQgKEZhY3R1cmUgRi0yMDI2LTAwMDEpIFRqIDAgLTI4IFRkIChHYWxlcmllIFNhaW50ZS1Bdm95ZSAtIGRvY3VtZW50IGRlIGRlbW9uc3RyYXRpb24pIFRqIEVUCmVuZHN0cmVhbQplbmRvYmoKNSAwIG9iago8PCAvVHlwZSAvRm9udCAvU3VidHlwZSAvVHlwZTEgL0Jhc2VGb250IC9IZWx2ZXRpY2EgPj4KZW5kb2JqCnhyZWYKMCA2CjAwMDAwMDAwMDAgNjU1MzUgZiAKMDAwMDAwMDAwOSAwMDAwMCBuIAowMDAwMDAwMDU4IDAwMDAwIG4gCjAwMDAwMDAxMTUgMDAwMDAgbiAKMDAwMDAwMDI0MSAwMDAwMCBuIAowMDAwMDAwNDA1IDAwMDAwIG4gCnRyYWlsZXIKPDwgL1NpemUgNiAvUm9vdCAxIDAgUiA+PgpzdGFydHhyZWYKNDc1CiUlRU9G';

/**
 * Les documents de galerie (S4-c, § 19) partagent le même PDF minimal que les
 * ventes : leur aperçu n'a lui non plus rien à voir avec le rendu réel, il ne
 * fait que prouver qu'une URL signée s'ouvre.
 */
export const DEMO_PDF_DOCUMENT_DATA_URI = DEMO_PDF_FACTURE_DATA_URI;

export const DEMO_PROFIL_FISCAL: ProfilFiscal = {
  assujetti: true,
  franchiseEnBase: false,
  enFranchise: false,
  numeroTva: 'FR40123456789',
  regimeParDefaut: 'normal',
  tauxNormalPointsDeBase: 2_000,
  tauxReduitPointsDeBase: 550,
  droitDeSuite: {
    seuilCentimes: 75_000,
    plafondCentimes: 1_250_000,
    tranches: [
      { plafondCentimes: 5_000_000, tauxPointsDeBase: 400 },
      { plafondCentimes: 20_000_000, tauxPointsDeBase: 300 },
      { plafondCentimes: 35_000_000, tauxPointsDeBase: 100 },
      { plafondCentimes: 50_000_000, tauxPointsDeBase: 50 },
      { plafondCentimes: null, tauxPointsDeBase: 25 },
    ],
  },
  droitDeSuiteAChargeAcheteur: false,
  droitDeSuiteBeneficiaire: 'ADAGP',
  mentions: {},
  delaiPaiementJours: 30,
  emetteur: {
    raisonSociale: 'Galerie Sainte-Avoye SARL',
    formeJuridique: 'SARL',
    capitalCentimes: 1_000_000,
    siren: '512345678',
    siret: '51234567800019',
    tvaIntracom: 'FR40512345678',
    rcs: 'Paris B 512 345 678',
    codeApe: '4778C',
    adresse: {
      libelle: 'Siège',
      ligne1: '6 rue de Seine',
      ligne2: '',
      codePostal: '75006',
      ville: 'Paris',
      pays: 'FR',
    },
    courriel: 'facturation@sainte-avoye.example',
    telephone: '+33 1 42 00 00 00',
    siteWeb: 'https://sainte-avoye.artosera.com',
    iban: 'FR7630006000011234567890189',
    bic: 'AGRIFRPP',
    banque: 'Crédit Agricole',
    mentionsComplementaires: [],
    escompte: 'Escompte pour paiement anticipé : néant.',
  },
  modifieLe: '2026-06-01T09:00:00.000Z',
};

export const DEMO_MODELE_FACTURE: ModeleDocument = {
  cle: 'facture',
  nom: 'Facture',
  logotypeMediaId: null,
  entete: { fr: 'Galerie Sainte-Avoye' },
  pied: { fr: 'Merci de votre confiance.' },
  mentions: [],
  couleurs: {
    encre: '#1A1A1A',
    papier: '#FFFFFF',
    accent: '#5B3A8C',
    sourdine: '#6B6B6B',
    filet: '#D8D2D2',
  },
  policeTitre: 'serif',
  policeTexte: 'sans',
  langue: 'fr',
  format: 'A4',
  actif: true,
  texteCertificat: null,
  signataire: null,
  lieuEmission: null,
  prixTtc: false,
  logoEtiquettes: false,
  modifieLe: '2026-06-01T09:00:00.000Z',
};

const CARTEL_COURTESY = {
  artiste: 'Léa Bertrand',
  titre: 'Courtesy',
  annee: '2023',
  technique: 'Huile sur toile',
  dimensions: '90 × 70 cm',
  numeroInventaire: 'SAV-2024-018',
};

const CARTEL_VERNISSAGE = {
  artiste: 'Hugo Massin',
  titre: 'Vernissage',
  annee: '2023',
  technique: 'Acrylique sur bois',
  dimensions: '60 × 60 cm',
  numeroInventaire: 'SAV-2023-004',
};

const CARTEL_HEURES_PALES = {
  artiste: 'Léa Bertrand',
  titre: 'Les heures pâles',
  annee: '2024',
  technique: 'Huile sur toile',
  dimensions: '130 × 97 cm',
  numeroInventaire: 'SAV-2024-017',
};

/** Facture brouillon : totaux recalculés côté API à chaque lecture (17.4). */
export const DEMO_FACTURE_BROUILLON: FacturePresentee = {
  id: 'facture-1',
  numero: null,
  type: 'facture',
  etat: 'brouillon',
  contactId: 'contact-4',
  contactNom: 'Bastien Ridel',
  factureOrigineId: null,
  lignes: [
    {
      designation: 'Léa Bertrand — Courtesy, 2023',
      quantite: 1,
      prixUnitaireCentimes: 820_000,
      remisePointsDeBase: 0,
      prixUnitaireNetCentimes: 820_000,
      regimeTva: 'marge',
      motifExoneration: null,
      droitDeSuite: false,
      oeuvreId: 'oeuvre-2',
      exemplaireId: null,
      cartel: CARTEL_COURTESY,
      prixAchatCentimes: 250_000,
    },
  ],
  remisePointsDeBase: 0,
  devise: 'EUR',
  langue: 'fr',
  droitDeSuiteAChargeAcheteur: false,
  mentions: [{ fr: 'TVA sur la marge (art. 297 A du CGI) — méthode de la marge bénéficiaire.' }],
  note: null,
  reference: null,
  modeReglement: 'virement',
  conditionsReglement: null,
  totaux: {
    totalHtCentimes: 820_000,
    totalTvaCentimes: 0,
    totalTtcCentimes: 820_000,
    droitDeSuiteCentimes: 0,
    parCategorieTva: [
      {
        categorie: 'E',
        regime: 'marge',
        tauxPointsDeBase: 0,
        baseCentimes: 820_000,
        tvaCentimes: 0,
      },
    ],
  },
  totauxFiges: false,
  version: 0,
  emiseLe: null,
  echeanceLe: null,
  dateLivraison: null,
  montantRegleCentimes: 0,
  resteADevoirCentimes: 820_000,
  pdfDisponible: false,
  facturXDisponible: false,
  creeLe: '2026-09-18T10:00:00.000Z',
  modifieLe: '2026-09-18T10:00:00.000Z',
};

/** Facture émise, réglée en partie. */
export const DEMO_FACTURE_EMISE: FacturePresentee = {
  id: 'facture-2',
  numero: 'F-2026-0001',
  type: 'facture',
  etat: 'emise',
  contactId: 'contact-1',
  contactNom: 'Étienne Lenoir',
  factureOrigineId: null,
  lignes: [
    {
      designation: 'Hugo Massin — Vernissage, 2023',
      quantite: 1,
      prixUnitaireCentimes: 450_000,
      remisePointsDeBase: 0,
      prixUnitaireNetCentimes: 450_000,
      regimeTva: 'normal',
      motifExoneration: null,
      droitDeSuite: false,
      oeuvreId: 'oeuvre-3',
      exemplaireId: null,
      cartel: CARTEL_VERNISSAGE,
      prixAchatCentimes: 150_000,
    },
  ],
  remisePointsDeBase: 0,
  devise: 'EUR',
  langue: 'fr',
  droitDeSuiteAChargeAcheteur: false,
  mentions: [],
  note: null,
  reference: 'Vente directe, galerie.',
  modeReglement: 'virement',
  conditionsReglement: '30 jours net.',
  totaux: {
    totalHtCentimes: 450_000,
    totalTvaCentimes: 90_000,
    totalTtcCentimes: 540_000,
    droitDeSuiteCentimes: 0,
    parCategorieTva: [
      {
        categorie: 'S',
        regime: 'normal',
        tauxPointsDeBase: 2_000,
        baseCentimes: 450_000,
        tvaCentimes: 90_000,
      },
    ],
  },
  totauxFiges: true,
  version: 0,
  emiseLe: '2026-08-14T00:00:00.000Z',
  echeanceLe: '2026-09-13T00:00:00.000Z',
  dateLivraison: null,
  montantRegleCentimes: 200_000,
  resteADevoirCentimes: 340_000,
  pdfDisponible: true,
  facturXDisponible: true,
  creeLe: '2026-08-12T09:00:00.000Z',
  modifieLe: '2026-08-14T09:05:00.000Z',
};

/** Facture payée intégralement, droit de suite refacturé à l'acheteur. */
export const DEMO_FACTURE_PAYEE: FacturePresentee = {
  id: 'facture-3',
  numero: 'F-2026-0002',
  type: 'facture',
  etat: 'payee',
  contactId: 'contact-2',
  contactNom: 'Fondation Meraux pour l’art contemporain',
  factureOrigineId: null,
  lignes: [
    {
      designation: 'Léa Bertrand — Les heures pâles, 2024',
      quantite: 1,
      prixUnitaireCentimes: 1_250_000,
      remisePointsDeBase: 0,
      prixUnitaireNetCentimes: 1_250_000,
      regimeTva: 'marge',
      motifExoneration: null,
      droitDeSuite: true,
      oeuvreId: 'oeuvre-1',
      exemplaireId: null,
      cartel: CARTEL_HEURES_PALES,
      prixAchatCentimes: 400_000,
    },
  ],
  remisePointsDeBase: 0,
  devise: 'EUR',
  langue: 'fr',
  droitDeSuiteAChargeAcheteur: true,
  mentions: [{ fr: 'Droit de suite dû par l’acheteur (art. L122-8 du CPI), reversé à l’ADAGP.' }],
  note: null,
  reference: null,
  modeReglement: 'virement',
  conditionsReglement: null,
  totaux: {
    totalHtCentimes: 1_250_000,
    totalTvaCentimes: 0,
    totalTtcCentimes: 1_275_000,
    droitDeSuiteCentimes: 25_000,
    parCategorieTva: [
      {
        categorie: 'E',
        regime: 'marge',
        tauxPointsDeBase: 0,
        baseCentimes: 1_250_000,
        tvaCentimes: 0,
      },
    ],
  },
  totauxFiges: true,
  version: 0,
  emiseLe: '2026-07-02T00:00:00.000Z',
  echeanceLe: '2026-08-01T00:00:00.000Z',
  dateLivraison: '2026-07-10T00:00:00.000Z',
  montantRegleCentimes: 1_275_000,
  resteADevoirCentimes: 0,
  pdfDisponible: true,
  facturXDisponible: true,
  creeLe: '2026-06-28T09:00:00.000Z',
  modifieLe: '2026-07-05T14:00:00.000Z',
};

/** Facture annulée par l'avoir ci-dessous. */
export const DEMO_FACTURE_ANNULEE: FacturePresentee = {
  id: 'facture-5',
  numero: 'F-2026-0003',
  type: 'facture',
  etat: 'annulee',
  contactId: 'contact-4',
  contactNom: 'Bastien Ridel',
  factureOrigineId: null,
  lignes: [
    {
      designation: 'Encadrement sur mesure',
      quantite: 1,
      prixUnitaireCentimes: 150_000,
      remisePointsDeBase: 0,
      prixUnitaireNetCentimes: 150_000,
      regimeTva: 'normal',
      motifExoneration: null,
      droitDeSuite: false,
      oeuvreId: null,
      exemplaireId: null,
      cartel: null,
      prixAchatCentimes: null,
    },
  ],
  remisePointsDeBase: 0,
  devise: 'EUR',
  langue: 'fr',
  droitDeSuiteAChargeAcheteur: false,
  mentions: [],
  note: null,
  reference: null,
  modeReglement: 'cheque',
  conditionsReglement: null,
  totaux: {
    totalHtCentimes: 150_000,
    totalTvaCentimes: 30_000,
    totalTtcCentimes: 180_000,
    droitDeSuiteCentimes: 0,
    parCategorieTva: [
      {
        categorie: 'S',
        regime: 'normal',
        tauxPointsDeBase: 2_000,
        baseCentimes: 150_000,
        tvaCentimes: 30_000,
      },
    ],
  },
  totauxFiges: true,
  version: 0,
  emiseLe: '2026-05-04T00:00:00.000Z',
  echeanceLe: '2026-06-03T00:00:00.000Z',
  dateLivraison: null,
  montantRegleCentimes: 0,
  resteADevoirCentimes: 0,
  pdfDisponible: true,
  facturXDisponible: true,
  creeLe: '2026-05-01T09:00:00.000Z',
  modifieLe: '2026-05-10T11:00:00.000Z',
};

/** L'avoir qui annule `DEMO_FACTURE_ANNULEE` (17.7). */
export const DEMO_AVOIR: FacturePresentee = {
  ...DEMO_FACTURE_ANNULEE,
  id: 'facture-4',
  numero: 'A-2026-0001',
  type: 'avoir',
  etat: 'emise',
  emiseLe: '2026-05-10T00:00:00.000Z',
  echeanceLe: null,
  factureOrigineId: 'facture-5',
  montantRegleCentimes: 0,
  resteADevoirCentimes: 180_000,
  creeLe: '2026-05-10T11:00:00.000Z',
  modifieLe: '2026-05-10T11:00:00.000Z',
};

export const DEMO_FACTURES: FacturePresentee[] = [
  DEMO_FACTURE_BROUILLON,
  DEMO_FACTURE_EMISE,
  DEMO_FACTURE_PAYEE,
  DEMO_AVOIR,
  DEMO_FACTURE_ANNULEE,
];

export const DEMO_REGLEMENTS: ReglementPresente[] = [
  {
    id: 'reglement-1',
    factureId: 'facture-2',
    factureNumero: 'F-2026-0001',
    montantCentimes: 200_000,
    devise: 'EUR',
    mode: 'virement',
    date: '2026-08-20T09:00:00.000Z',
    reference: 'VIR-20260820',
    note: null,
    creeLe: '2026-08-20T09:05:00.000Z',
  },
  {
    id: 'reglement-2',
    factureId: 'facture-3',
    factureNumero: 'F-2026-0002',
    montantCentimes: 700_000,
    devise: 'EUR',
    mode: 'virement',
    date: '2026-07-08T09:00:00.000Z',
    reference: 'VIR-20260708',
    note: 'Acompte à la commande.',
    creeLe: '2026-07-08T09:05:00.000Z',
  },
  {
    id: 'reglement-3',
    factureId: 'facture-3',
    factureNumero: 'F-2026-0002',
    montantCentimes: 575_000,
    devise: 'EUR',
    mode: 'cheque',
    date: '2026-07-14T09:00:00.000Z',
    reference: null,
    note: 'Solde à la livraison.',
    creeLe: '2026-07-14T09:05:00.000Z',
  },
];

/**
 * Certificats de démonstration (S4-c, § 19.5) : un déjà émis pour « Les
 * heures pâles » (`oeuvre-1`, pièce unique — pas d'exemplaire à attester),
 * pour que la section Certificats de la fiche œuvre ait quelque chose à
 * montrer sans qu'il faille d'abord en émettre un. `demo.ts` y ajoute ceux
 * émis en session, mutable, comme il le fait pour les pages du site.
 */
export const DEMO_CERTIFICATS_INITIAUX: CertificatPresente[] = [
  {
    id: 'certificat-1',
    adresseVerification: 'https://demonstration.invalid/verifier/00000000000000000000000000',
    annuleLe: null,
    motifPublic: null,
    numero: 'C-2026-0001',
    oeuvreId: 'oeuvre-1',
    exemplaireId: null,
    exemplaire: null,
    langue: 'fr',
    emisLe: '2026-06-02T10:00:00.000Z',
    cartel: {
      artiste: 'Léa Bertrand',
      titre: 'Les heures pâles',
      annee: '2024',
      technique: 'Huile sur toile',
      dimensions: '130 × 97 cm',
      numeroInventaire: 'SAV-2024-017',
    },
    provenance: ['Atelier de l’artiste'],
    signataire: { nom: 'Claire Vallier', qualite: 'Directrice' },
    lieuEmission: 'Paris',
    documentPret: true,
  },
];

/**
 * Médias de la galerie, à plat, pour le sélecteur de logotype (S6-f) : la
 * charte pointe vers un média EXISTANT (§ 15.6), il n'existe pas de cible de
 * dépose dédiée à un logo (§ 15.11 n'ouvre que `artwork` / `artist` /
 * `exhibition` / `post`). Réutilise les vignettes déjà posées pour les
 * œuvres.
 */
export const DEMO_MEDIAS_GALERIE: MediaPresente[] = DEMO_OEUVRES.filter(
  (oeuvre) => oeuvre.mediaPrincipalId !== null,
).map((oeuvre) => ({
  id: oeuvre.mediaPrincipalId as string,
  cleOriginale: `demo/${oeuvre.slug}.jpg`,
  contentType: 'image/svg+xml',
  nomFichier: `${oeuvre.slug}.svg`,
  octets: 0,
  largeur: 640,
  hauteur: 800,
  variantes: [],
  type: 'vue',
  rattachement: { type: 'artwork', id: oeuvre.id },
  credit: null,
  legende: { fr: oeuvre.titre },
  ordre: 0,
  supprime: false,
  creeLe: '2024-01-01T09:00:00.000Z',
  modifieLe: '2024-01-01T09:00:00.000Z',
}));

/**
 * Site de la galerie de démonstration (S6-f) : réglages, pages fixes,
 * publications. Reprend le catalogue ci-dessus (mêmes artistes, œuvres et
 * expositions) pour un aperçu cohérent.
 */
export const DEMO_SITE: ReponseSite = {
  site: {
    theme: 'cimaise',
    charte: {
      logotypeMediaId: null,
      logotypeHauteur: null,
      couleurPrincipale: '#2d2a3d',
      couleurSecondaire: '#f4f1ea',
      policeTitres: 'familjen-grotesk',
      policeTexte: 'newsreader',
    },
    policesAdmises: {
      titres: ['familjen-grotesk', 'archivo', 'dm-sans'],
      texte: ['newsreader', 'karla', 'dm-sans'],
    },
    accueil: [
      { type: 'exposition-en-cours' },
      { type: 'oeuvres', limite: 8 },
      { type: 'agenda', limite: 4 },
      { type: 'actualites', limite: 3 },
    ],
    langues: ['fr', 'en'],
    langueParDefaut: 'fr',
    menus: {
      principal: [
        { rubrique: 'accueil' },
        { rubrique: 'artistes' },
        { rubrique: 'expositions' },
        { rubrique: 'actualites' },
        { rubrique: 'la-galerie' },
        { rubrique: 'contact' },
      ],
      pied: [
        { rubrique: 'mentions' },
        { rubrique: 'confidentialite' },
        { rubrique: 'plan-du-site' },
      ],
    },
    options: { oeuvresDisponibles: true, formulaireContact: true, newsletter: false },
    identite: {
      baseline: {
        fr: 'Peinture et sculpture contemporaines, depuis 2015.',
        en: 'Contemporary painting and sculpture, since 2015.',
      },
      description: {
        fr: 'La Galerie Sainte-Avoye présente une scène française émergente : peinture, bronze, estampe.',
        en: 'Galerie Sainte-Avoye presents an emerging French scene: painting, bronze, print.',
      },
      email: 'contact@sainte-avoye.example',
      telephone: '+33 1 42 71 30 12',
      adresses: [
        {
          usage: 'principale',
          rue: '12 rue Sainte-Avoye',
          codePostal: '75003',
          ville: 'Paris',
          pays: 'FR',
        },
      ],
      horaires: {
        plages: [
          { du: 'mardi', au: 'samedi', creneaux: [{ ouverture: '11:00', fermeture: '19:00' }] },
        ],
        note: { fr: 'Fermé les jours fériés.' },
      },
      reseaux: [
        {
          reseau: 'instagram',
          libelle: '@galeriesainteavoye',
          url: 'https://instagram.com/galeriesainteavoye',
        },
      ],
    },
    mentionArtosera: true,
    mentionDesactivable: true,
    redirections: [
      {
        de: '/ancienne-expo',
        vers: '/fr/expositions/lumieres-declinantes/',
        code: 301,
        poseeLe: '2026-05-01T09:00:00.000Z',
      },
    ],
    dernierePublication: '2026-09-20T08:12:00.000Z',
    versionPubliee: 3,
    modifieLe: '2026-09-20T09:00:00.000Z',
  },
  domaine: 'sainte-avoye.artosera.gallery',
};

export const DEMO_PAGES: PageSite[] = [
  {
    id: 'page-galerie',
    rubrique: 'la-galerie',
    cle: 'galerie',
    slug: 'la-galerie',
    titre: { fr: 'La galerie', en: 'The gallery' },
    contenu: {
      fr:
        'Fondée en 2015, la Galerie Sainte-Avoye défend une scène française émergente, entre ' +
        'peinture, bronze et estampe.\n\nElle représente Léa Bertrand et Hugo Massin, et suit le ' +
        'travail d’Inès Auber.',
      en:
        'Founded in 2015, Galerie Sainte-Avoye champions an emerging French scene, across ' +
        'painting, bronze and print.',
    },
    equipe: [
      {
        nom: 'Claire Vallier',
        role: { fr: 'Direction', en: 'Director' },
        email: 'direction@sainte-avoye.example',
      },
      { nom: 'Marc Le Goff', role: { fr: 'Régie', en: 'Registrar' } },
    ],
    appelNewsletter: null,
    ordre: 1,
    visibleSurLeSite: true,
    modifieLe: '2026-06-01T09:00:00.000Z',
  },
  {
    id: 'page-contact',
    rubrique: 'contact',
    cle: 'contact',
    slug: 'contact',
    titre: { fr: 'Contact', en: 'Contact' },
    contenu: {
      fr: 'Écrivez-nous. Nous répondons sous deux jours ouvrés.\n\nVisite en dehors des horaires, sur rendez-vous.',
      en: 'Write to us. We reply within two business days.',
    },
    equipe: [],
    appelNewsletter: {
      fr: 'Recevez nos expositions et nos actualités.',
      en: 'Get our shows and news.',
    },
    ordre: 2,
    visibleSurLeSite: true,
    modifieLe: '2026-06-01T09:00:00.000Z',
  },
  {
    id: 'page-mentions',
    rubrique: 'mentions',
    cle: 'mentions',
    slug: 'mentions-legales',
    titre: { fr: 'Mentions légales', en: 'Legal notice' },
    contenu: {
      fr: 'Galerie Sainte-Avoye SARL, 12 rue Sainte-Avoye, 75003 Paris. RCS Paris 812 345 678.\n\nCe site est hébergé dans l’Union européenne par Artosera.',
    },
    equipe: [],
    appelNewsletter: null,
    ordre: 3,
    visibleSurLeSite: true,
    modifieLe: '2024-01-05T09:00:00.000Z',
  },
  {
    id: 'page-confidentialite',
    rubrique: 'confidentialite',
    cle: 'confidentialite',
    slug: 'confidentialite',
    titre: { fr: 'Confidentialité', en: 'Privacy' },
    contenu: {
      fr: 'Ce site ne dépose aucun traceur publicitaire.\n\nLes informations envoyées par le formulaire de contact servent à vous répondre.',
    },
    equipe: [],
    appelNewsletter: null,
    ordre: 4,
    visibleSurLeSite: true,
    modifieLe: '2024-01-05T09:00:00.000Z',
  },
];

export const DEMO_PUBLICATIONS: PublicationPresentee[] = [
  {
    id: 'publication-3',
    version: 3,
    dossier: '000003-20260920T081200Z',
    etat: 'reussie',
    raison: 'site.reglages',
    debuteLe: '2026-09-20T08:11:42.000Z',
    termineLe: '2026-09-20T08:12:00.000Z',
    dureeMs: 18_000,
    fichiers: 214,
    medias: 18,
    polices: 6,
    octets: 48_302_114,
    hotes: ['sainte-avoye.artosera.gallery', 'www.sainte-avoye.example'],
    erreur: null,
  },
  {
    id: 'publication-2',
    version: 2,
    dossier: null,
    etat: 'echouee',
    raison: 'site.page',
    debuteLe: '2026-09-18T14:03:10.000Z',
    termineLe: '2026-09-18T14:03:16.000Z',
    dureeMs: 6_000,
    fichiers: 0,
    medias: 0,
    polices: 0,
    octets: 0,
    hotes: [],
    erreur: 'Site incomplet : adresse de courriel manquante (identite.email).',
  },
  {
    id: 'publication-1',
    version: 1,
    dossier: '000001-20260601T090500Z',
    etat: 'reussie',
    raison: 'publication demandée',
    debuteLe: '2026-06-01T09:04:40.000Z',
    termineLe: '2026-06-01T09:05:00.000Z',
    dureeMs: 20_000,
    fichiers: 186,
    medias: 14,
    polices: 6,
    octets: 39_884_002,
    hotes: ['sainte-avoye.artosera.gallery'],
    erreur: null,
  },
];

/**
 * Contrat de données du site (§ 15.9) : assemblé à la main plutôt que dérivé
 * des fixtures ci-dessus — les deux modèles divergent volontairement (écarts
 * documentés dans `donnees.ts`) et cette forme est celle que `render()`
 * consomme réellement, pour l'aperçu côté client en mode démonstration.
 */
export const DEMO_SITE_DATA: SiteData = {
  version: { numero: 3, publieLe: '2026-09-20T08:12:00.000Z' },
  galerie: {
    nom: 'Galerie Sainte-Avoye',
    slug: 'sainte-avoye',
    domaine: 'sainte-avoye.artosera.gallery',
    baseline: {
      fr: 'Peinture et sculpture contemporaines, depuis 2015.',
      en: 'Contemporary painting and sculpture, since 2015.',
    },
    description: {
      fr: 'La Galerie Sainte-Avoye présente une scène française émergente : peinture, bronze, estampe.',
      en: 'Galerie Sainte-Avoye presents an emerging French scene: painting, bronze, print.',
    },
    langues: { defaut: 'fr', actives: ['fr', 'en'] },
    adresses: [
      {
        usage: 'principale',
        rue: '12 rue Sainte-Avoye',
        codePostal: '75003',
        ville: 'Paris',
        pays: 'FR',
      },
    ],
    horaires: {
      plages: [
        { du: 'mardi', au: 'samedi', creneaux: [{ ouverture: '11:00', fermeture: '19:00' }] },
      ],
      note: { fr: 'Fermé les jours fériés.' },
    },
    email: 'contact@sainte-avoye.example',
    telephone: '+33 1 42 71 30 12',
    reseaux: [
      {
        reseau: 'instagram',
        libelle: '@galeriesainteavoye',
        url: 'https://instagram.com/galeriesainteavoye',
      },
    ],
    mentionArtosera: true,
  },
  charte: {
    theme: 'cimaise',
    couleurPrincipale: '#2d2a3d',
    couleurSecondaire: '#f4f1ea',
    policeTitres: 'familjen-grotesk',
    policeTexte: 'newsreader',
    accueil: [
      { type: 'exposition-en-cours' },
      { type: 'oeuvres', limite: 8 },
      { type: 'agenda', limite: 4 },
      { type: 'actualites', limite: 3 },
    ],
  },
  options: { oeuvresDisponibles: true, formulaireContact: true, newsletter: false },
  menus: {
    principal: [
      { type: 'rubrique', rubrique: 'accueil' },
      { type: 'rubrique', rubrique: 'artistes' },
      { type: 'rubrique', rubrique: 'expositions' },
      { type: 'rubrique', rubrique: 'actualites' },
      { type: 'rubrique', rubrique: 'la-galerie' },
      { type: 'rubrique', rubrique: 'contact' },
    ],
    pied: [
      { type: 'rubrique', rubrique: 'mentions' },
      { type: 'rubrique', rubrique: 'confidentialite' },
      { type: 'rubrique', rubrique: 'plan-du-site' },
    ],
  },
  artistes: [
    {
      slug: 'lea-bertrand',
      nom: 'Léa Bertrand',
      mention: { fr: 'née en 1987 à Rennes', en: 'born 1987 in Rennes' },
      bio: {
        fr: 'Léa Bertrand peint la lumière déclinante des intérieurs bourgeois, entre mémoire et disparition.',
        en: 'Léa Bertrand paints the fading light of bourgeois interiors, between memory and disappearance.',
      },
      represente: true,
      oeuvres: ['les-heures-pales'],
      expositions: ['lumieres-declinantes'],
      presse: [],
    },
    {
      slug: 'hugo-massin',
      nom: 'Hugo Massin',
      mention: { fr: 'né en 1979 à Lyon', en: 'born 1979 in Lyon' },
      bio: {
        fr: 'Hugo Massin construit ses toiles comme des accrochages : citations, cadrages, vernissages fantômes.',
      },
      represente: true,
      oeuvres: ['vernissage'],
      expositions: ['accrochages-fantomes'],
      presse: [],
    },
    {
      slug: 'ines-auber',
      nom: 'Inès Auber',
      mention: { fr: 'née en 1991 à Marseille' },
      bio: { fr: 'Inès Auber travaille le bronze et l’estampe, en séries courtes et numérotées.' },
      represente: false,
      oeuvres: ['consignation'],
      expositions: ['art-paris-2026'],
      presse: [],
    },
  ],
  oeuvres: [
    {
      slug: 'les-heures-pales',
      titre: 'Les heures pâles',
      artiste: 'lea-bertrand',
      annee: 2024,
      technique: { fr: 'Huile sur toile', en: 'Oil on canvas' },
      dimensions: { hauteur: 130, largeur: 97, unite: 'cm' },
      statut: 'reservee',
      visible: true,
      prix: { montantCentimes: 1_250_000, devise: 'EUR' },
      prixVisible: true,
      description: {
        fr: 'Une pièce de la série « Intérieurs » : la lumière de fin d’après-midi sur un salon vidé de ses meubles.',
        en: 'A piece from the “Interiors” series: late-afternoon light on a living room emptied of its furniture.',
      },
      medias: [],
    },
    {
      slug: 'vernissage',
      titre: 'Vernissage',
      artiste: 'hugo-massin',
      annee: 2023,
      technique: { fr: 'Acrylique sur bois', en: 'Acrylic on wood' },
      dimensions: { hauteur: 60, largeur: 60, unite: 'cm' },
      statut: 'vendue',
      visible: true,
      prixVisible: false,
      description: { fr: 'Un accrochage vu depuis la réserve, cadré par la porte entrebâillée.' },
      medias: [],
    },
    {
      slug: 'consignation',
      titre: 'Consignation',
      artiste: 'ines-auber',
      annee: 2024,
      technique: { fr: 'Bronze, épreuve 2/8', en: 'Bronze, edition 2/8' },
      dimensions: { hauteur: 55, largeur: 22, profondeur: 18, unite: 'cm' },
      edition: { tirage: 8, epreuvesArtiste: 2 },
      statut: 'en-depot',
      visible: true,
      prix: { montantCentimes: 3_400_000, devise: 'EUR' },
      prixVisible: true,
      description: {
        fr: 'Épreuve en dépôt chez un confrère parisien, retour prévu en fin de trimestre.',
      },
      medias: [],
    },
  ],
  expositions: [
    {
      slug: 'lumieres-declinantes',
      type: 'exposition',
      titre: { fr: 'Lumières déclinantes', en: 'Fading Lights' },
      dateDebut: '2026-10-08',
      dateFin: '2026-11-14',
      vernissage: { date: '2026-10-08', debut: '18:30', fin: '21:00' },
      lieu: { nom: 'Galerie Sainte-Avoye — Salle 2', ville: 'Paris', pays: 'FR' },
      texte: {
        fr: 'Une exposition personnelle de Léa Bertrand : intérieurs, mémoire, lumière qui se retire.',
        en: 'A solo show by Léa Bertrand: interiors, memory, retreating light.',
      },
      artistes: ['lea-bertrand'],
      oeuvres: ['les-heures-pales'],
      vuesAccrochage: [],
      etat: 'a-venir',
    },
    {
      slug: 'accrochages-fantomes',
      type: 'exposition',
      titre: { fr: 'Accrochages fantômes', en: 'Ghost Hangings' },
      dateDebut: '2026-09-02',
      dateFin: '2026-09-30',
      lieu: { nom: 'Galerie Sainte-Avoye — Salle 2', ville: 'Paris', pays: 'FR' },
      texte: { fr: 'Hugo Massin cite, cadre, rejoue le vernissage — jusqu’à son effacement.' },
      artistes: ['hugo-massin'],
      oeuvres: ['vernissage'],
      vuesAccrochage: [],
      etat: 'en-cours',
    },
    {
      slug: 'art-paris-2026',
      type: 'foire',
      titre: { fr: 'Foire de la Cimaise 2026' },
      dateDebut: '2026-04-02',
      dateFin: '2026-04-06',
      lieu: {
        nom: 'Grand Palais',
        adresse: '3 avenue du Général Eisenhower',
        ville: 'Paris',
        pays: 'FR',
      },
      stand: 'B12',
      texte: { fr: 'Stand collectif : bronzes et estampes d’Inès Auber.' },
      artistes: ['ines-auber'],
      oeuvres: ['consignation'],
      vuesAccrochage: [],
      etat: 'passee',
    },
  ],
  actualites: [
    {
      slug: 'lea-bertrand-ouvre-lumieres-declinantes',
      titre: { fr: 'Léa Bertrand ouvre « Lumières déclinantes »' },
      date: '2026-09-20',
      chapo: { fr: 'Vernissage le 8 octobre, dès 18h30.' },
      texte: { fr: 'La galerie présente la nouvelle série de Léa Bertrand à partir du 8 octobre.' },
      artistes: ['lea-bertrand'],
      expositions: ['lumieres-declinantes'],
    },
  ],
  pages: {
    laGalerie: {
      titre: { fr: 'La galerie', en: 'The gallery' },
      corps: {
        fr: 'Fondée en 2015, la Galerie Sainte-Avoye défend une scène française émergente, entre peinture, bronze et estampe.',
        en: 'Founded in 2015, Galerie Sainte-Avoye champions an emerging French scene, across painting, bronze and print.',
      },
      equipe: [
        {
          nom: 'Claire Vallier',
          role: { fr: 'Direction', en: 'Director' },
          email: 'direction@sainte-avoye.example',
        },
        { nom: 'Marc Le Goff', role: { fr: 'Régie', en: 'Registrar' } },
      ],
    },
    contact: {
      titre: { fr: 'Contact', en: 'Contact' },
      corps: {
        fr: 'Écrivez-nous. Nous répondons sous deux jours ouvrés.',
        en: 'Write to us. We reply within two business days.',
      },
      newsletter: {
        fr: 'Recevez nos expositions et nos actualités.',
        en: 'Get our shows and news.',
      },
    },
    mentions: {
      titre: { fr: 'Mentions légales', en: 'Legal notice' },
      corps: {
        fr: 'Galerie Sainte-Avoye SARL, 12 rue Sainte-Avoye, 75003 Paris. RCS Paris 812 345 678.',
      },
    },
    confidentialite: {
      titre: { fr: 'Confidentialité', en: 'Privacy' },
      corps: { fr: 'Ce site ne dépose aucun traceur publicitaire.' },
    },
  },
  redirections: [
    { de: '/ancienne-expo', vers: '/fr/expositions/lumieres-declinantes', statut: 301 },
  ],
};

// ── Coffre (3.5, S5) ──────────────────────────────────────────────────────────

/**
 * Le coffre de démonstration couvre les échéances en retard et à venir,
 * les documents confidentiels, les versions et les archives sans backend.
 */
export const DEMO_DOCUMENTS: DocumentPresente[] = [
  {
    id: 'doc-1',
    type: 'contrat_representation',
    titre: 'Contrat de représentation — Léa Bertrand',
    confidentialite: 'confidentiel',
    fichier: {
      cle: 'documents/doc-1/v1.pdf',
      nom: 'contrat-representation-lea-bertrand.pdf',
      typeMime: 'application/pdf',
      octets: 412_300,
      depose: '2026-04-01T09:00:00.000Z',
      deposePar: 'demo-compte-1',
    },
    versions: [],
    rattachements: [{ type: 'artist', id: 'artiste-1' }],
    echeanceLe: '2027-04-01T00:00:00.000Z',
    rappelsJours: [30, 7, 1],
    rappelsEnvoyes: [],
    etiquettes: ['exclusivite', 'representation'],
    archive: false,
    archiveLe: null,
    creeLe: '2026-04-01T09:00:00.000Z',
    modifieLe: '2026-04-01T09:00:00.000Z',
  },
  {
    id: 'doc-2',
    type: 'certificat',
    titre: 'Certificat d’authenticité — Les heures pâles',
    confidentialite: 'equipe',
    fichier: {
      cle: 'documents/doc-2/v1.pdf',
      nom: 'certificat-les-heures-pales.pdf',
      typeMime: 'application/pdf',
      octets: 98_400,
      depose: '2026-06-12T10:30:00.000Z',
      deposePar: 'demo-compte-1',
    },
    versions: [],
    rattachements: [{ type: 'artwork', id: 'oeuvre-1' }],
    echeanceLe: null,
    rappelsJours: [],
    rappelsEnvoyes: [],
    etiquettes: [],
    archive: false,
    archiveLe: null,
    creeLe: '2026-06-12T10:30:00.000Z',
    modifieLe: '2026-09-21T16:40:00.000Z',
  },
  {
    id: 'doc-3',
    type: 'assurance',
    titre: 'Police d’assurance transport 2026',
    confidentialite: 'equipe',
    fichier: {
      cle: 'documents/doc-3/v1.pdf',
      nom: 'assurance-transport-2026.pdf',
      typeMime: 'application/pdf',
      octets: 210_500,
      depose: '2025-10-05T08:00:00.000Z',
      deposePar: 'demo-compte-2',
    },
    versions: [],
    rattachements: [{ type: 'exhibition', id: 'exposition-1' }],
    echeanceLe: '2026-10-05T00:00:00.000Z',
    rappelsJours: [30, 7, 1],
    rappelsEnvoyes: [30],
    etiquettes: ['transport', '2026'],
    archive: false,
    archiveLe: null,
    creeLe: '2025-10-05T08:00:00.000Z',
    modifieLe: '2025-10-05T08:00:00.000Z',
  },
  {
    id: 'doc-4',
    type: 'contrat_depot',
    titre: 'Contrat de dépôt — Étienne Lenoir',
    confidentialite: 'equipe',
    fichier: {
      cle: 'documents/doc-4/v1.pdf',
      nom: 'contrat-depot-etienne-lenoir.pdf',
      typeMime: 'application/pdf',
      octets: 156_700,
      depose: '2025-09-10T11:00:00.000Z',
      deposePar: 'demo-compte-1',
    },
    versions: [],
    rattachements: [{ type: 'contact', id: 'contact-1' }],
    echeanceLe: '2026-09-10T00:00:00.000Z',
    rappelsJours: [30, 7, 1],
    rappelsEnvoyes: [30, 7, 1],
    etiquettes: ['depot'],
    archive: false,
    archiveLe: null,
    creeLe: '2025-09-10T11:00:00.000Z',
    modifieLe: '2025-09-10T11:00:00.000Z',
  },
  {
    id: 'doc-5',
    type: 'bon_transport',
    titre: 'Bon de transport — Foire de la Cimaise',
    confidentialite: 'equipe',
    fichier: {
      cle: 'documents/doc-5/v2.pdf',
      nom: 'bon-transport-art-basel-v2.pdf',
      typeMime: 'application/pdf',
      octets: 64_200,
      depose: '2026-08-30T09:15:00.000Z',
      deposePar: 'demo-compte-2',
    },
    versions: [
      {
        cle: 'documents/doc-5/v1.pdf',
        nom: 'bon-transport-art-basel-v1.pdf',
        typeMime: 'application/pdf',
        octets: 60_100,
        depose: '2026-08-12T09:00:00.000Z',
        deposePar: 'demo-compte-2',
      },
    ],
    rattachements: [{ type: 'exhibition', id: 'exposition-1' }],
    echeanceLe: '2026-10-12T00:00:00.000Z',
    rappelsJours: [],
    rappelsEnvoyes: [],
    etiquettes: ['transport'],
    archive: false,
    archiveLe: null,
    creeLe: '2026-08-12T09:00:00.000Z',
    modifieLe: '2026-08-30T09:15:00.000Z',
  },
  {
    id: 'doc-7',
    type: 'contrat_depot',
    titre: 'Convention de prêt — Collection Dumas',
    confidentialite: 'equipe',
    fichier: {
      cle: 'documents/doc-7/v1.pdf',
      nom: 'convention-pret-collection-dumas.pdf',
      typeMime: 'application/pdf',
      octets: 184_200,
      depose: '2026-03-18T09:00:00.000Z',
      deposePar: 'demo-compte-1',
    },
    versions: [],
    rattachements: [{ type: 'contact', id: 'contact-2' }],
    echeanceLe: '2026-09-18T00:00:00.000Z',
    rappelsJours: [30, 7, 1],
    rappelsEnvoyes: [30, 7, 1],
    etiquettes: ['pret'],
    archive: false,
    archiveLe: null,
    creeLe: '2026-03-18T09:00:00.000Z',
    modifieLe: '2026-03-18T09:00:00.000Z',
  },
  {
    id: 'doc-8',
    type: 'assurance',
    titre: 'Attestation d’assurance — Atelier Ravel',
    confidentialite: 'equipe',
    fichier: {
      cle: 'documents/doc-8/v1.pdf',
      nom: 'attestation-assurance-atelier-ravel.pdf',
      typeMime: 'application/pdf',
      octets: 122_600,
      depose: '2026-03-23T10:30:00.000Z',
      deposePar: 'demo-compte-2',
    },
    versions: [],
    rattachements: [{ type: 'artist', id: 'artiste-2' }],
    echeanceLe: '2026-09-23T00:00:00.000Z',
    rappelsJours: [30, 7, 1],
    rappelsEnvoyes: [30, 7, 1],
    etiquettes: ['assurance'],
    archive: false,
    archiveLe: null,
    creeLe: '2026-03-23T10:30:00.000Z',
    modifieLe: '2026-03-23T10:30:00.000Z',
  },
  {
    id: 'doc-9',
    type: 'autre',
    titre: 'Autorisation de reproduction — Léa Bertrand',
    confidentialite: 'equipe',
    fichier: {
      cle: 'documents/doc-9/v1.pdf',
      nom: 'autorisation-reproduction-lea-bertrand.pdf',
      typeMime: 'application/pdf',
      octets: 86_700,
      depose: '2026-08-22T14:00:00.000Z',
      deposePar: 'demo-compte-1',
    },
    versions: [],
    rattachements: [{ type: 'artist', id: 'artiste-1' }],
    echeanceLe: '2026-10-22T00:00:00.000Z',
    rappelsJours: [30, 7, 1],
    rappelsEnvoyes: [30],
    etiquettes: ['droits'],
    archive: false,
    archiveLe: null,
    creeLe: '2026-08-22T14:00:00.000Z',
    modifieLe: '2026-08-22T14:00:00.000Z',
  },
  {
    id: 'doc-6',
    type: 'autre',
    titre: 'Ancien bail commercial',
    confidentialite: 'equipe',
    fichier: {
      cle: 'documents/doc-6/v1.pdf',
      nom: 'ancien-bail-commercial.pdf',
      typeMime: 'application/pdf',
      octets: 302_000,
      depose: '2023-01-04T09:00:00.000Z',
      deposePar: 'demo-compte-1',
    },
    versions: [],
    rattachements: [],
    echeanceLe: null,
    rappelsJours: [],
    rappelsEnvoyes: [],
    etiquettes: ['bail'],
    archive: true,
    archiveLe: '2024-03-01T09:00:00.000Z',
    creeLe: '2023-01-04T09:00:00.000Z',
    modifieLe: '2024-03-01T09:00:00.000Z',
  },
];

/** Journal de `doc-2` (§ 18.6) : qui, quoi, quand, adresse. */
export const DEMO_DOCUMENT_ACCESS: AccesPresente[] = [
  {
    id: 'acces-1',
    documentId: 'doc-2',
    action: 'ouvert',
    date: '2026-09-20T09:12:00.000Z',
    adresse: '82.65.12.4',
    cle: 'documents/doc-2/v1.pdf',
    compteId: 'demo-compte-1',
    compteEmail: 'direction@sainte-avoye.example',
  },
  {
    id: 'acces-2',
    documentId: 'doc-2',
    action: 'telecharge',
    date: '2026-09-20T09:13:05.000Z',
    adresse: '82.65.12.4',
    cle: 'documents/doc-2/v1.pdf',
    compteId: 'demo-compte-1',
    compteEmail: 'direction@sainte-avoye.example',
  },
  {
    id: 'acces-3',
    documentId: 'doc-2',
    action: 'ouvert',
    date: '2026-09-21T14:02:11.000Z',
    adresse: '90.12.44.201',
    cle: 'documents/doc-2/v1.pdf',
    compteId: 'demo-compte-2',
    compteEmail: 'marc.legoff@sainte-avoye.example',
  },
  {
    id: 'acces-4',
    documentId: 'doc-2',
    action: 'modifie',
    date: '2026-09-21T16:40:00.000Z',
    adresse: '82.65.12.4',
    cle: 'documents/doc-2/v1.pdf',
    compteId: 'demo-compte-1',
    compteEmail: 'direction@sainte-avoye.example',
  },
];

/** Équipe de démonstration (`GET …/equipe`, S2-k) : les noms du journal. */
export const DEMO_EQUIPE: MembreEquipe[] = [
  {
    compteId: 'demo-compte-1',
    nom: 'Claire Vallier',
    email: 'direction@sainte-avoye.example',
    role: 'direction',
    droits: {
      voirPrixArtiste: true,
      coffreConfidentiel: true,
      emettreFactures: true,
      publierSite: true,
    },
    support: false,
    expireLe: null,
    derniereConnexion: '2026-09-21T16:40:00.000Z',
  },
  {
    compteId: 'demo-compte-2',
    nom: 'Marc Le Goff',
    email: 'marc.legoff@sainte-avoye.example',
    role: 'equipe',
    droits: {
      voirPrixArtiste: false,
      coffreConfidentiel: false,
      emettreFactures: false,
      publierSite: false,
    },
    support: false,
    expireLe: null,
    derniereConnexion: '2026-09-21T14:02:11.000Z',
  },
  {
    compteId: 'demo-compte-3',
    nom: 'Rim Bouazizi',
    email: 'rim.bouazizi@sainte-avoye.example',
    role: 'equipe',
    droits: {
      voirPrixArtiste: true,
      coffreConfidentiel: false,
      emettreFactures: false,
      publierSite: false,
    },
    support: false,
    expireLe: null,
    derniereConnexion: '2026-09-22T09:00:00.000Z',
  },
  {
    compteId: 'demo-compte-4',
    nom: 'Claire Nadaud',
    email: 'claire.nadaud@sainte-avoye.example',
    role: 'comptable',
    droits: {
      voirPrixArtiste: false,
      coffreConfidentiel: false,
      emettreFactures: false,
      publierSite: false,
    },
    support: false,
    expireLe: null,
    derniereConnexion: '2026-09-22T09:00:00.000Z',
  },
  {
    compteId: 'demo-compte-5',
    nom: 'Yann Delorme',
    email: 'yann.delorme@sainte-avoye.example',
    role: 'lecture',
    droits: {
      voirPrixArtiste: false,
      coffreConfidentiel: false,
      emettreFactures: false,
      publierSite: false,
    },
    support: false,
    expireLe: null,
    derniereConnexion: '2026-09-22T09:00:00.000Z',
  },
];

/** Visible uniquement dans les captures avec le module SSO explicitement choisi. */
export const DEMO_CONNECTEURS: ReponseConnecteurs = {
  connecteurs: [
    {
      id: 'oidc',
      module: 'sso',
      mode: 'simule',
      configure: true,
      finSecret: null,
      revision: 1,
      verifieLe: null,
      erreur: null,
      parametres: {
        emetteur: 'https://id.sainte-avoye.example',
        clientId: 'artosera-demo',
        domainesAutorises: 'sainte-avoye.example',
        libelleBouton: 'Compte Sainte-Avoye',
        emailSimule: 'direction@sainte-avoye.example',
      },
    },
  ],
  champs: {
    oidc: {
      secrets: [{ cle: 'clientSecret', requis: false, type: 'texte' }],
      parametres: [
        { cle: 'emetteur', requis: true, type: 'url' },
        { cle: 'clientId', requis: true, type: 'texte' },
        { cle: 'domainesAutorises', requis: true, type: 'liste-domaines' },
        { cle: 'libelleBouton', requis: false, type: 'texte' },
        { cle: 'emailSimule', requis: false, type: 'email' },
      ],
    },
  },
  simulation: true,
};
/** Données dormantes : aucune activation de module ici. */
export const DEMO_MOUVEMENTS: MouvementPresente[] = (
  ['depot', 'pret', 'sortie', 'depot'] as const
).map((type, index) => {
  const oeuvre =
    DEMO_OEUVRES.find(
      (o) => o.statut === (index === 0 ? 'en_depot' : index === 1 ? 'pretee' : 'disponible'),
    ) ?? DEMO_OEUVRES[index];
  if (!oeuvre) throw new Error('Œuvre de démonstration manquante.');
  const etat = index < 2 ? 'en_cours' : index === 2 ? 'prevu' : 'termine';
  const date = '2026-09-01T00:00:00.000Z';
  return {
    id: `mouvement-${String(index + 1)}`,
    type,
    etat,
    enRetard: index === 0,
    joursDeRetard: index === 0 ? 12 : 0,
    lignes: [
      {
        oeuvreId: oeuvre.id,
        titre: oeuvre.titre,
        artisteNom: oeuvre.artisteNom,
        numeroInventaire: oeuvre.numeroInventaire,
        mediaPrincipalId: oeuvre.mediaPrincipalId,
        ...(oeuvre.mediaPrincipalId && DEMO_VIGNETTES[oeuvre.mediaPrincipalId]
          ? { apercuUrl: DEMO_VIGNETTES[oeuvre.mediaPrincipalId] }
          : {}),
        prixPublicCentimes: oeuvre.prixPublicCentimes,
        statut: oeuvre.statut,
        localisationAvantId: 'lieu-1',
        statutAvant: 'disponible',
        statutPose: index === 0 ? 'en_depot' : index === 1 ? 'pretee' : null,
        retourneeLe: index === 3 ? date : null,
        ignoreeAuRetour: null,
      },
    ],
    deId: 'lieu-1',
    versId: index === 2 ? 'lieu-3' : 'lieu-4',
    contactId: 'contact-1',
    contactNom: DEMO_CONTACTS[0]?.nomAffiche ?? '',
    transporteurId: null,
    departLe: date,
    retourPrevuLe: index === 0 ? '2026-09-20T00:00:00.000Z' : '2027-01-05T00:00:00.000Z',
    retourReelLe: index === 3 ? date : null,
    assurance:
      index === 1
        ? {
            valeurCentimes: 500000,
            devise: 'EUR',
            police: 'POL-DEMO',
            assureur: 'Assurances exemple',
          }
        : null,
    valeurProposeeCentimes: oeuvre.prixPublicCentimes ?? 0,
    suivi: { transporteurNom: 'Transports exemple', numeroSuivi: 'TR-DEMO' },
    bon:
      index === 2
        ? null
        : {
            numero: index === 1 ? 'BP-2026-0001' : index === 0 ? 'BD-2026-0003' : 'BD-2026-0002',
            emisLe: date,
            documentId: null,
          },
    constats:
      index === 0
        ? [
            {
              id: 'constat-1',
              oeuvreId: oeuvre.id,
              moment: 'depart',
              etatGeneral: 'usure',
              observations: 'Léger frottement au coin inférieur.',
              etabliLe: date,
              etabliPar: 'Régie',
              photos: [],
              documentId: null,
            },
          ]
        : [],
    note: '',
    annuleLe: null,
    creeLe: date,
    modifieLe: date,
  };
});

/** Trois propositions fictives ; leur présence n’active aucun module. */
export const DEMO_OFFRES: OffrePresentee[] = [
  { id: 'offre-1', etat: 'brouillon', contactId: 'contact-2', oeuvreId: 'oeuvre-2', canal: null },
  { id: 'offre-2', etat: 'envoyee', contactId: 'contact-3', oeuvreId: 'oeuvre-3', canal: 'stand' },
  {
    id: 'offre-3',
    etat: 'acceptee',
    contactId: 'contact-1',
    oeuvreId: 'oeuvre-1',
    canal: 'courriel',
  },
].map((entree, index) => {
  const oeuvre = DEMO_OEUVRES.find((o) => o.id === entree.oeuvreId)!;
  const prix = oeuvre.prixPublicCentimes ?? 1_800_000;
  const maintenant = new Date();
  return {
    id: entree.id,
    numero: `O-${maintenant.getFullYear()}-000${index + 1}`,
    contactId: entree.contactId,
    contactNom: DEMO_CONTACTS.find((c) => c.id === entree.contactId)!.nomAffiche,
    expositionId: null,
    lignes: [
      {
        oeuvreId: oeuvre.id,
        exemplaireId: null,
        designation: `${oeuvre.artisteNom} — ${oeuvre.titre}`,
        cartel: {
          artiste: oeuvre.artisteNom,
          titre: oeuvre.titre,
          ...(oeuvre.annee === null ? {} : { annee: String(oeuvre.annee) }),
          numeroInventaire: oeuvre.numeroInventaire,
        },
        prixPublicCentimes: oeuvre.prixPublicCentimes,
        prixProposeCentimes: prix,
      },
    ],
    totalCentimes: prix,
    devise: oeuvre.devise,
    langue: 'fr',
    validiteJusquau: new Date(maintenant.getTime() + 14 * 86_400_000).toISOString().slice(0, 10),
    message: 'À la suite de votre visite, voici notre proposition pour cette œuvre.',
    etat: entree.etat as EtatOffre,
    canal: entree.canal as OffrePresentee['canal'],
    echue: false,
    envoyeeLe: index === 0 ? null : new Date(maintenant.getTime() - 2 * 86_400_000).toISOString(),
    relancePrevueLe:
      index === 1 ? new Date(maintenant.getTime() - 86_400_000).toISOString().slice(0, 10) : null,
    relanceeLe: null,
    accepteeLe: index === 2 ? maintenant.toISOString() : null,
    refuseeLe: null,
    retireeLe: null,
    expireeLe: null,
    motif: '',
    envois: [],
    reservationIds: index === 2 ? ['reservation-1'] : [],
    factureId: null,
    creeeLe: new Date(maintenant.getTime() - 3 * 86_400_000).toISOString(),
    modifieeLe: maintenant.toISOString(),
  };
});

// Agenda et presse (M6) : données dormantes, activation réservée à la console.
export const DEMO_RENDEZ_VOUS: RendezVousPresente[] = [
  {
    id: 'rdv-1',
    titre: 'Visite de la réserve',
    debut: '2026-10-12T08:00:00.000Z',
    fin: '2026-10-12T09:00:00.000Z',
    journeeEntiere: false,
    lieu: 'Galerie',
    contactId: 'contact-1',
    oeuvreIds: [],
    expositionId: null,
    participants: ['demo-compte-1'],
    note: '',
    rappel: true,
    annuleLe: null,
  },
  {
    id: 'rdv-2',
    titre: 'Réunion d’accrochage',
    debut: '2026-10-16T12:00:00.000Z',
    fin: '2026-10-16T13:00:00.000Z',
    journeeEntiere: false,
    lieu: 'Salle principale',
    contactId: null,
    oeuvreIds: [],
    expositionId: 'exposition-1',
    participants: [],
    note: '',
    rappel: true,
    annuleLe: null,
  },
];
export const DEMO_AGENDA: EvenementAgenda[] = [
  {
    id: 'expositions:exposition-1:periode',
    source: 'expositions',
    sorte: 'exposition',
    titre: DEMO_EXPOSITIONS[0]!.titre.fr,
    debut: '2026-10-08',
    fin: '2026-11-14',
    journeeEntiere: true,
    lieu: null,
    lien: { type: 'exposition', id: 'exposition-1' },
  },
  {
    id: 'expositions:exposition-1:vernissage',
    source: 'expositions',
    sorte: 'vernissage',
    titre: `Vernissage — ${DEMO_EXPOSITIONS[0]!.titre.fr}`,
    debut: '2026-10-08T16:30:00.000Z',
    fin: '2026-10-08T19:00:00.000Z',
    journeeEntiere: false,
    lieu: null,
    lien: { type: 'exposition', id: 'exposition-1' },
  },
  ...DEMO_RENDEZ_VOUS.map((r) => ({
    id: `rendez-vous:${r.id}:rdv`,
    source: 'rendez-vous',
    sorte: 'rendez-vous' as const,
    titre: r.titre,
    debut: r.debut,
    fin: r.fin,
    journeeEntiere: r.journeeEntiere,
    lieu: r.lieu,
    lien: { type: 'rendez-vous' as const, id: r.id },
  })),
  {
    id: 'coffre:document-1:echeance',
    source: 'coffre',
    sorte: 'echeance',
    titre: 'Assurance des œuvres',
    debut: '2026-10-20',
    fin: null,
    journeeEntiere: true,
    lieu: null,
    lien: { type: 'document', id: DEMO_DOCUMENTS[0]!.id },
  },
];

/** Supports, auteurs et contenus fictifs de la revue de presse M6. */
export const DEMO_PRESSE: ArticlePresente[] = [
  ...[
    'Une lumière retenue',
    'Les cimaises en mouvement',
    'Paris, un regard à hauteur d’œuvre',
    'Le dessin prend son temps',
  ].map((titre, i) => ({
    id: `article-${i + 1}`,
    genre: 'article' as const,
    titre,
    support: [
      'La Revue des cimaises',
      'Journal des arts fictif',
      'Atelier imaginaire',
      'Carnet des regards',
    ][i]!,
    auteur: ['Anne Clair', 'Marc Lenoir', 'Eva Duval', 'Paul Marin'][i]!,
    paruLe: `2026-10-${String(2 + i * 4).padStart(2, '0')}`,
    url: 'https://presse.example/article',
    langue: 'fr' as const,
    extrait:
      'Une matière sensible, un geste retenu : le parcours de l’exposition invite à prendre le temps de regarder. Texte de démonstration fictif.',
    isbn: '',
    pages: null,
    expositions: ['exposition-1'],
    artistes: ['artiste-1'],
    oeuvres: [],
    fichiers:
      i === 0
        ? [
            {
              rang: 0,
              nom: 'Coupure — Une lumière retenue.pdf',
              typeMime: 'application/pdf',
              octets: 24800,
              depose: '2026-10-02T08:00:00Z',
            },
          ]
        : [],
    archiveLe: null,
  })),
  {
    id: 'catalogue-1',
    genre: 'catalogue',
    titre: 'Le temps suspendu — catalogue',
    support: 'Éditions Sainte-Avoye (fictives)',
    auteur: 'La galerie',
    paruLe: '2026-10-08',
    url: '',
    langue: 'fr',
    extrait: 'Publication de quatre-vingt-seize pages accompagnant l’exposition.',
    isbn: '',
    pages: 96,
    expositions: ['exposition-1'],
    artistes: ['artiste-1'],
    oeuvres: [],
    fichiers: [],
    archiveLe: null,
  },
];
export {
  DEMO_MODELES_CONTRATS,
  DEMO_CONTRATS,
  DEMO_CONTRATS_ARTISTES,
} from './demoContratsFixtures.js';
// M4 : données dormantes, visibles uniquement avec les modules effectifs.
export const DEMO_PROVENANCE: Record<string, ProvenancePresentee[]> = {
  'oeuvre-1': [
    {
      id: 'provenance-1',
      oeuvreId: 'oeuvre-1',
      exemplaireId: null,
      rang: 0,
      type: 'proprietaire',
      libelle: 'Atelier de Léa Bertrand',
      contactId: null,
      contactNom: null,
      periode: '2024',
      mode: 'atelier',
      source: 'Atelier',
      public: true,
      note: '',
      ligne: 'Atelier de Léa Bertrand, 2024',
    },
    {
      id: 'provenance-2',
      oeuvreId: 'oeuvre-1',
      exemplaireId: null,
      rang: 1,
      type: 'exposition',
      libelle: 'Galerie Sainte-Avoye, Les heures pâles',
      contactId: null,
      contactNom: null,
      periode: '2025',
      mode: null,
      source: 'Archives de la galerie',
      public: false,
      note: '',
      ligne: 'Galerie Sainte-Avoye, Les heures pâles, 2025',
    },
  ],
};
export const DEMO_COUTS: Record<string, CoutPresente[]> = {
  'oeuvre-1': [
    {
      id: 'cout-1',
      exemplaireId: null,
      categorie: 'encadrement',
      libelle: 'Cadre en chêne',
      montantCentimes: 28000,
      devise: 'EUR',
      date: '2025-06-04',
      fournisseur: 'Atelier du cadre (fictif)',
      note: '',
    },
    {
      id: 'cout-2',
      exemplaireId: null,
      categorie: 'transport',
      libelle: 'Transport atelier–galerie',
      montantCentimes: 15000,
      devise: 'EUR',
      date: '2025-06-12',
      fournisseur: 'Transporteur fictif',
      note: '',
    },
  ],
};
export const DEMO_DOSSIERS_OEUVRES: DossierOeuvresPresente[] = [
  {
    id: 'dossier-1',
    titre: 'Sélection pour la foire',
    contactId: 'contact-1',
    oeuvres: ['oeuvre-1', 'oeuvre-2', 'oeuvre-3'],
    message: 'Une sélection de peintures et de sculptures pour votre collection.',
    langue: 'fr',
    avecPrix: true,
    avecProvenance: true,
    archiveLe: null,
    creeLe: '2026-09-30T10:00:00Z',
    modifieLe: '2026-09-30T10:00:00Z',
  },
];
/** Console fictive pour les captures locales explicitement ouvertes. */
export const DEMO_CONSOLE_GROUPE: ConsoleGroupe = {
  groupe: {
    id: 'demo-groupe-1',
    slug: 'galeries-allen',
    nom: 'Galeries Allen',
    role: 'direction_groupe',
  },
  depuis: '2026-01-01',
  partages: { chiffres: true, oeuvres: true, ventes: true, echeancesCoffre: true, contacts: false },
  galeries: [
    {
      id: 'demo-galerie-1',
      slug: 'sainte-avoye',
      nom: 'Galerie Sainte-Avoye',
      incluse: true,
      chiffres: { oeuvres: 128, artistes: 14 },
      stock: [{ devise: 'EUR', centimes: 4820000, nombre: 41 }],
      ventes: {
        factures: [{ devise: 'EUR', centimes: 2135000, nombre: 12 }],
        ventesSimples: [{ devise: 'EUR', centimes: 180000, nombre: 2 }],
      },
      echeances: [
        {
          id: 'gd1',
          type: 'assurance',
          titre: null,
          confidentiel: true,
          echeanceLe: '2026-10-20T00:00:00.000Z',
        },
        {
          id: 'gd2',
          type: 'contrat_depot',
          titre: 'Dépôt — collection Ravel',
          confidentiel: false,
          echeanceLe: '2026-11-02T00:00:00.000Z',
        },
        {
          id: 'gd3',
          type: 'autre',
          titre: 'Bail de la réserve',
          confidentiel: false,
          echeanceLe: '2026-12-01T00:00:00.000Z',
        },
      ],
    },
    {
      id: 'demo-galerie-2',
      slug: 'maison-vallier',
      nom: 'Maison Vallier',
      incluse: true,
      chiffres: { oeuvres: 86, artistes: 9 },
      stock: [
        { devise: 'EUR', centimes: 3200000, nombre: 28 },
        { devise: 'CHF', centimes: 450000, nombre: 3 },
      ],
      ventes: {
        factures: [{ devise: 'EUR', centimes: 1265000, nombre: 7 }],
        ventesSimples: [{ devise: 'EUR', centimes: 90000, nombre: 1 }],
      },
      echeances: [],
    },
    {
      id: 'demo-galerie-3',
      slug: 'brun-et-fils',
      nom: 'Brun et Fils',
      incluse: false,
      raison: 'module_inactif',
    },
  ],
  totaux: {
    stock: [
      { devise: 'CHF', centimes: 450000, nombre: 3 },
      { devise: 'EUR', centimes: 8020000, nombre: 69 },
    ],
    factures: [{ devise: 'EUR', centimes: 3400000, nombre: 19 }],
    ventesSimples: [{ devise: 'EUR', centimes: 270000, nombre: 3 }],
  },
};
const oeuvreSalleDemo = (id: string): OeuvreDeLaSalle => {
  const o = DEMO_OEUVRES.find((oeuvre) => oeuvre.id === id)!;
  return {
    id: o.id,
    titre: o.titre,
    artisteNom: o.artisteNom,
    annee: o.annee ?? null,
    statut: o.statut,
    mediaPrincipalId: o.mediaPrincipalId,
    prixPublicCentimes: o.prixPublicCentimes ?? null,
    devise: o.devise,
  };
};
const dateSalleDemo = (jours: number) => new Date(Date.now() + jours * 86_400_000).toISOString();
export const DEMO_VIEWING_ROOMS: SallePresentee[] = [
  {
    id: 'salle-demo-1',
    titre: {
      fr: `Pour ${DEMO_CONTACTS[0]!.nomAffiche} — accrochage d’automne`,
      en: `For ${DEMO_CONTACTS[0]!.nomAffiche} — autumn selection`,
    },
    texte: {
      fr: 'Trois œuvres réunies pour leur lumière et leur présence. Une sélection personnelle à découvrir à votre rythme.',
    },
    oeuvres: ['oeuvre-2', 'oeuvre-3', 'oeuvre-4'].map(oeuvreSalleDemo),
    oeuvresManquantes: 0,
    contact: { id: DEMO_CONTACTS[0]!.id, nomAffiche: DEMO_CONTACTS[0]!.nomAffiche },
    etat: 'active',
    expireLe: dateSalleDemo(10),
    prixVisibles: true,
    protegee: true,
    hoteLien: 'sainte-avoye.artosera.gallery',
    lienEmisLe: dateSalleDemo(-4),
    revoqueeLe: null,
    ouvertures: {
      total: 7,
      visiteurs: 3,
      premiereLe: dateSalleDemo(-3),
      derniereLe: dateSalleDemo(-0.1),
      journal: Array.from({ length: 7 }, (_, i) => ({
        date: dateSalleDemo(-0.1 - i * 0.4),
        visiteur: ['a91b3c', 'b274d1', 'c713ef'][i % 3]!,
      })),
    },
    creeLe: dateSalleDemo(-4),
    modifieLe: dateSalleDemo(-1),
  },
  {
    id: 'salle-demo-2',
    titre: { fr: 'Sélection pour le FRAC', en: 'Selection for the FRAC' },
    texte: null,
    oeuvres: ['oeuvre-5', 'oeuvre-6'].map(oeuvreSalleDemo),
    oeuvresManquantes: 0,
    contact: null,
    etat: 'expiree',
    expireLe: dateSalleDemo(-3),
    prixVisibles: false,
    protegee: false,
    hoteLien: null,
    lienEmisLe: null,
    revoqueeLe: null,
    ouvertures: { total: 0, visiteurs: 0, premiereLe: null, derniereLe: null, journal: [] },
    creeLe: dateSalleDemo(-20),
    modifieLe: dateSalleDemo(-20),
  },
];

/** M8 : données fictives ; aucun secret exploitable. */
export const DEMO_JETONS: JetonPresente[] = [
  {
    id: 'jeton-demo-actif',
    nom: 'Logiciel comptable (démonstration)',
    prefixe: 'arto_pat_demo',
    scopes: ['read:accounting', 'read:invoices'],
    etat: 'ACTIF',
    expireLe: '2027-01-01T00:00:00Z',
    creeLe: '2026-09-01T09:00:00Z',
    creePar: 'direction@sainte-avoye.example',
    dernierUsageLe: '2026-10-01T10:00:00Z',
    revoqueLe: null,
  },
  {
    id: 'jeton-demo-revoque',
    nom: 'Ancien export',
    prefixe: 'arto_pat_anci',
    scopes: ['read:invoices'],
    etat: 'REVOQUE',
    expireLe: '2026-09-01T00:00:00Z',
    creeLe: '2026-06-01T09:00:00Z',
    creePar: 'direction@sainte-avoye.example',
    dernierUsageLe: null,
    revoqueLe: '2026-07-01T00:00:00Z',
  },
];

// M9 : fixtures fictives dormantes, visibles seulement avec modules=tous.
export const DEMO_NEWSLETTER_LISTES: ListeNewsletter[] = [
  {
    id: 'liste-1',
    nom: 'Collectionneurs',
    description: 'Le premier regard sur les œuvres nouvelles.',
    regle: { typesContact: ['collectionneur'], etiquettes: [], langues: [] },
    membres: 24,
    synchronisee: true,
    archive: false,
    creeLe: '2026-09-01T08:00:00Z',
    modifieLe: '2026-09-01T08:00:00Z',
  },
  {
    id: 'liste-2',
    nom: 'Institutions et presse',
    description: 'Expositions et actualité de la galerie.',
    regle: { typesContact: ['institution', 'presse'], etiquettes: [], langues: [] },
    membres: 9,
    synchronisee: true,
    archive: false,
    creeLe: '2026-09-01T08:00:00Z',
    modifieLe: '2026-09-01T08:00:00Z',
  },
  {
    id: 'liste-3',
    nom: 'Lecteurs anglophones',
    description: 'La lettre en anglais.',
    regle: { typesContact: [], etiquettes: [], langues: ['en'] },
    membres: 6,
    synchronisee: false,
    archive: false,
    creeLe: '2026-09-01T08:00:00Z',
    modifieLe: '2026-09-01T08:00:00Z',
  },
];
export const DEMO_NEWSLETTER_CAMPAGNES: CampagneNewsletter[] = [
  {
    id: 'campagne-1',
    titre: 'Rentrée à la galerie',
    sujet: 'La rentrée à Sainte-Avoye',
    langue: 'fr',
    listes: ['liste-1'],
    blocs: [
      { type: 'texte', texte: 'Trois œuvres pour ouvrir la saison.' },
      { type: 'oeuvre', oeuvreId: 'oeuvre-4' },
    ],
    etat: 'envoyee',
    envoiDemandeLe: '2026-09-10T08:00:00Z',
    envoyeeLe: '2026-09-10T08:00:00Z',
    statistiques: {
      envoyes: 186,
      delivres: 181,
      ouvertures: 97,
      clics: 24,
      desinscriptions: 1,
      rebonds: 5,
      luesLe: '2026-09-11T08:00:00Z',
    },
    erreur: null,
    revision: 2,
    creeLe: '2026-09-08T08:00:00Z',
    modifieLe: '2026-09-10T08:00:00Z',
  },
  {
    id: 'campagne-2',
    titre: 'Foire de novembre',
    sujet: 'Retrouvons-nous à la foire',
    langue: 'fr',
    listes: ['liste-1', 'liste-2'],
    blocs: [
      { type: 'texte', texte: 'Deux regards sur la matière, à découvrir sur notre stand.' },
      { type: 'oeuvre', oeuvreId: 'oeuvre-4' },
      { type: 'oeuvre', oeuvreId: 'oeuvre-2' },
    ],
    etat: 'brouillon',
    envoiDemandeLe: null,
    envoyeeLe: null,
    statistiques: null,
    erreur: null,
    revision: 0,
    creeLe: '2026-10-01T08:00:00Z',
    modifieLe: '2026-10-01T08:00:00Z',
  },
];
export const DEMO_NEWSLETTER_VISUELS: VisuelNewsletter[] = [
  {
    id: 'visuel-1',
    cible: { type: 'oeuvre', id: 'oeuvre-4' },
    format: 'carre',
    etat: 'pret',
    octets: 812000,
    erreur: null,
    creeLe: '2026-10-01T08:00:00Z',
  },
  {
    id: 'visuel-2',
    cible: { type: 'oeuvre', id: 'oeuvre-2' },
    format: 'vertical',
    etat: 'pret',
    octets: 964000,
    erreur: null,
    creeLe: '2026-10-01T08:00:00Z',
  },
];
export const DEMO_NEWSLETTER_REGLAGES: ReglagesNewsletter = {
  connecteur: {
    configure: true,
    mode: 'simule',
    expediteur: { nom: 'Galerie Sainte-Avoye', email: 'lettre@sainte-avoye.demo' },
  },
  webhook: { branche: true, brancheLe: '2026-09-01T08:00:00Z' },
  derniereSynchro: { le: '2026-10-01T08:00:00Z', poussees: 39, retirees: 2, desinscrits: 1 },
};

/** Fixtures de capture uniquement : le semis serveur garde les flux fermés. */
export const DEMO_DIFFUSION_FLUX: FluxDiffusion[] = [
  {
    id: 'demo-flux-artsy',
    plateforme: 'artsy',
    actif: true,
    toutesLesEligibles: false,
    oeuvresChoisies: DEMO_OEUVRES.slice(0, 8).map((o) => o.id),
    langue: 'en',
    lien: true,
    lienCreeLe: '2026-10-01T09:00:00Z',
    revision: 1,
    dernierDepot: {
      reference: 'demo-depot',
      empreinte: 'demo-empreinte',
      deposeLe: '2026-10-02T08:00:00Z',
      nombre: 12,
      statut: 'publie',
      rejets: [],
    },
    derniereErreur: null,
  },
  {
    id: 'demo-flux-artnet',
    plateforme: 'artnet',
    actif: false,
    toutesLesEligibles: true,
    oeuvresChoisies: [],
    langue: 'en',
    lien: false,
    lienCreeLe: null,
    revision: 1,
    dernierDepot: null,
    derniereErreur: null,
  },
];
export const DEMO_DIFFUSION_ETATS: EtatOeuvreDiffusion[] = Array.from({ length: 17 }, (_, i) => ({
  plateforme: 'artsy',
  oeuvreId: DEMO_OEUVRES[i % DEMO_OEUVRES.length]!.id,
  reference: DEMO_OEUVRES[i % DEMO_OEUVRES.length]!.numeroInventaire,
  etat: i < 12 ? 'diffusee' : i < 14 ? 'refusee' : 'retiree',
  erreurs:
    i === 12
      ? [{ code: 'dimensions_manquantes', champ: 'dimensions' }]
      : i === 13
        ? [{ code: 'image_manquante', champ: 'mediaPrincipalId' }]
        : [],
  diffuseeLe: i < 12 ? '2026-10-02T08:00:00Z' : null,
  retireeLe: i >= 14 ? '2026-10-02T08:00:00Z' : null,
  majLe: '2026-10-02T08:00:00Z',
}));
export const DEMO_IDENTITES_SSO: IdentiteSso[] = [
  {
    id: 'demo-identite-sso-1',
    compteId: 'demo-compte-1',
    email: 'direction@sainte-avoye.example',
    emetteur: 'https://id.sainte-avoye.example',
    lieLe: '2026-10-01T08:00:00.000Z',
    derniereConnexionLe: '2026-10-01T08:00:00.000Z',
  },
];

export const DEMO_REGLAGES_BOUTIQUE: ReglagesBoutique = {
  ouverte: false,
  conditionsValidees: false,
  conditionsValideesLe: null,
  conditionsVente: {
    fr: 'Modèle à faire valider\nLa galerie confirme la disponibilité de l’œuvre et convient avec l’acheteur de sa remise ou de son expédition. Les modalités, délais, frais et droits applicables doivent être complétés et validés avant ouverture.',
    en: 'Template to be reviewed\nThe gallery confirms availability and arranges collection or shipping with the buyer. Terms, deadlines, charges and applicable rights must be completed and reviewed before opening.',
  },
};
export const DEMO_OEUVRES_BOUTIQUE: OeuvreBoutique[] = [
  {
    id: 'oeuvre-1',
    titre: 'Les heures pâles',
    artisteNom: 'Lise Marchal',
    statut: 'disponible',
    mediaPrincipalId: null,
    apercuUrl: heuresPales,
    enVente: true,
    raison: null,
    prixTtcCentimes: 840000,
  },
  {
    id: 'oeuvre-2',
    titre: 'Relevé',
    artisteNom: 'Adrien Courtois',
    statut: 'disponible',
    mediaPrincipalId: null,
    apercuUrl: releve,
    enVente: false,
    raison: null,
    prixTtcCentimes: 360000,
  },
  {
    id: 'oeuvre-3',
    titre: 'Bassin',
    artisteNom: 'Lise Marchal',
    statut: 'disponible',
    mediaPrincipalId: null,
    apercuUrl: bassin,
    enVente: false,
    raison: 'hors_ligne',
    prixTtcCentimes: null,
  },
  {
    id: 'oeuvre-4',
    titre: 'Étude pour une rivière',
    artisteNom: 'Adrien Courtois',
    statut: 'disponible',
    mediaPrincipalId: null,
    enVente: false,
    raison: 'edition_au_compteur',
    prixTtcCentimes: null,
  },
];
export const DEMO_COMMANDES_BOUTIQUE: CommandePresentee[] = [
  {
    id: 'commande-1',
    reference: 'jF0tB2-9mD8sQ3kP7rV4cA',
    etat: 'payee',
    motif: null,
    oeuvre: {
      id: 'oeuvre-1',
      titre: 'Les heures pâles',
      artisteNom: 'Lise Marchal',
      mediaPrincipalId: null,
      apercuUrl: heuresPales,
    },
    exemplaire: null,
    contact: { id: 'contact-1', nomAffiche: 'Camille et Jules Laurent' },
    montantCentimes: 840000,
    devise: 'EUR',
    expireLe: '2026-10-01T11:31:00Z',
    payeeLe: '2026-10-01T11:04:00Z',
    paiementId: 'pi_demonstration_boutique',
    facture: { id: 'facture-1', numero: 'F-2026-0042' },
    avoir: null,
    creeLe: '2026-10-01T11:00:00Z',
  },
  {
    id: 'commande-2',
    reference: 'pK2mX7-4qD0bR8vS1jA6eQ',
    etat: 'en_paiement',
    motif: null,
    oeuvre: {
      id: 'oeuvre-2',
      titre: 'Relevé',
      artisteNom: 'Adrien Courtois',
      mediaPrincipalId: null,
      apercuUrl: releve,
    },
    exemplaire: null,
    contact: { id: 'contact-2', nomAffiche: 'Iris Delorme' },
    montantCentimes: 360000,
    devise: 'EUR',
    expireLe: '2026-10-02T14:31:00Z',
    payeeLe: null,
    paiementId: null,
    facture: null,
    avoir: null,
    creeLe: '2026-10-02T14:00:00Z',
  },
  {
    id: 'commande-3',
    reference: 'xG3aT8-2nH9cL4wF6sE0uR',
    etat: 'annulee',
    motif: 'oeuvre_indisponible',
    oeuvre: {
      id: 'oeuvre-3',
      titre: 'Bassin',
      artisteNom: 'Lise Marchal',
      mediaPrincipalId: null,
      apercuUrl: bassin,
    },
    exemplaire: null,
    contact: { id: 'contact-3', nomAffiche: 'Nora Petit' },
    montantCentimes: 580000,
    devise: 'EUR',
    expireLe: '2026-09-30T10:31:00Z',
    payeeLe: null,
    paiementId: null,
    facture: null,
    avoir: null,
    creeLe: '2026-09-30T10:00:00Z',
  },
];

export const DEMO_CATALOGUES_VISITE: CatalogueVisitePresente[] = DEMO_EXPOSITIONS.map(
  (exposition) => {
    const jeton = `demo${exposition.id.replaceAll('-', '_').padEnd(39, '_')}`;
    const url = `https://sainte-avoye.artosera.gallery/visite/${jeton}`;
    const oeuvres = exposition.oeuvres.flatMap((id) => {
      const oeuvre = DEMO_OEUVRES.find((candidate) => candidate.id === id);
      if (!oeuvre) return [];
      const jetonOeuvre = `demo${id.replaceAll('-', '_').padEnd(39, '_')}`;
      const prixAffiche = oeuvre.prixVisible && (oeuvre.prixPublicCentimes ?? 0) > 0;
      return [
        {
          oeuvreId: id,
          jeton: jetonOeuvre,
          titre: oeuvre.titre,
          artisteNom: oeuvre.artisteNom,
          annee: oeuvre.annee,
          statut: oeuvre.statut,
          mediaPrincipalId: oeuvre.mediaPrincipalId,
          masquee: false,
          prix: 'herite' as const,
          texteSalle: { fr: '', en: '' },
          prixAffiche,
          prixPublicCentimes: prixAffiche ? oeuvre.prixPublicCentimes : null,
          devise: oeuvre.devise,
          servie: oeuvre.statut !== 'archivee' && oeuvre.statut !== 'retiree',
          url: `${url}/${jetonOeuvre}`,
        },
      ];
    });
    return {
      id: `catalogue-${exposition.id}`,
      existe: true,
      exposition: {
        id: exposition.id,
        titre: exposition.titre,
        debutLe: exposition.debutLe,
        finLe: exposition.finLe,
      },
      publie: false,
      visibleJusquAu:
        echeanceParDefaut(exposition.finLe ? new Date(exposition.finLe) : null)?.toISOString() ??
        null,
      jeton,
      prix: 'regle_oeuvre',
      texte: { fr: '', en: '' },
      texteEffectif: { fr: exposition.texte?.fr ?? '', en: exposition.texte?.en ?? '' },
      accuseReception: false,
      oeuvres,
      nombreOeuvresServies: oeuvres.filter((oeuvre) => oeuvre.servie).length,
      regenereLe: null,
      revision: 1,
      etat: 'brouillon',
      url,
    };
  },
);
